<template>
  <v-container fluid class="rv2-wrap reports-container pa-0">
    <!-- Modern Header (matches legacy Reports page) -->
    <div
      v-if="showHeader"
      class="header-section mb-4"
      style="margin: 0 12px; position: relative;"
    >
      <!-- Close Button -->
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-chart-line</v-icon>
        </div>
        <div class="flex-grow-1">
          <h1 class="page-title mb-1">Reports and Analytics</h1>
          <p class="page-subtitle mb-0">
            Master reports with advanced filtering and analytics
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex align-center">
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-chart-box</v-icon>
            Analytics
          </v-chip>
          <v-chip
            v-if="!$store.state.user.permissions.disableExportList"
            class="mr-3 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-export</v-icon>
            Exportable
          </v-chip>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0" justify="start">
        <v-col cols="6" sm="4" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-filter</v-icon>
            <span>Advanced Filters</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-chart-donut</v-icon
            >
            <span>Visual Charts</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-table</v-icon>
            <span>Data Tables</span>
          </div>
        </v-col>
        <v-col cols="6" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-calendar-range</v-icon
            >
            <span>Date Range</span>
          </div>
        </v-col>
        <v-col v-if="!$store.state.user.permissions.disableExportList" cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-download</v-icon>
            <span>Export Data</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Action bar: view toggle + Export -->
    <div class="rv2-action-bar px-3 mb-3">
      <button
        type="button"
        class="classic-view-toggle classic-view-toggle-active rv2-classic-view-toggle"
        role="switch"
        aria-checked="true"
        @click="goToClassicReports"
      >
        <span class="classic-view-toggle-label classic-view-classic-label"
          >Classic View</span
        >
        <span class="classic-view-switch" aria-hidden="true">
          <span class="classic-view-switch-thumb"></span>
        </span>
        <span class="classic-view-toggle-label classic-view-modern-label"
          >Modern View</span
        >
      </button>
    </div>

    <div class="px-3">

    <!-- Filters bar -->
    <v-card class="rv2-card rv2-filter-card mb-4" outlined>
      <v-card-text class="pa-4">
        <div class="rv2-section-title mb-3">
          <v-icon small class="mr-1">mdi-tune-variant</v-icon>
          Filters
        </div>
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="selectedList"
              :items="lists"
              item-text="name"
              item-value="id"
              label="List"
              placeholder="All lists"
              outlined
              dense
              hide-details
              multiple
              small-chips
              deletable-chips
              clearable
              class="rv2-filter"
              @change="load"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index === 0" label x-small>
                  <span>{{ item.name }}</span>
                </v-chip>
                <span v-if="index === 1" class="grey--text caption">
                  (+{{ selectedList.length - 1 }} more)
                </span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="selectedUser"
              :items="allUsers"
              item-text="name"
              item-value="id"
              label="Member"
              placeholder="All members"
              outlined
              dense
              hide-details
              multiple
              small-chips
              deletable-chips
              clearable
              class="rv2-filter"
              @change="load"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index === 0" label x-small>
                  <span>{{ item.name }}</span>
                </v-chip>
                <span v-if="index === 1" class="grey--text caption">
                  (+{{ selectedUser.length - 1 }} more)
                </span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="selectedDispos"
              :items="calcDispo"
              item-text="title"
              item-value="title"
              label="Disposition"
              placeholder="All dispositions"
              outlined
              dense
              hide-details
              multiple
              small-chips
              deletable-chips
              clearable
              class="rv2-filter"
              @change="load"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index === 0" label x-small>
                  <span>{{ item.title }}</span>
                </v-chip>
                <span v-if="index === 1" class="grey--text caption">
                  (+{{ selectedDispos.length - 1 }} more)
                </span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="selectedCallDirection"
              :items="callDirections"
              label="Dialer type"
              outlined
              dense
              hide-details
              class="rv2-filter"
              @change="load"
            />
          </v-col>
        </v-row>

        <!-- Date preset row -->
        <div class="rv2-presets mt-3 d-flex flex-wrap align-center">
          <span class="rv2-quick-label mr-2">Quick range:</span>
          <v-btn
            v-for="p in quickRangePresets"
            :key="p.key"
            x-small
            depressed
            :outlined="quickRange !== p.key"
            :color="quickRange === p.key ? 'primary' : ''"
            class="rv2-preset mr-2 mb-1"
            @click="applyQuickRange(p.key)"
          >
            {{ p.label }}
          </v-btn>
          <v-dialog
            ref="dialog"
            v-model="modal"
            :return-value.sync="dates"
            persistent
            width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                x-small
                depressed
                :outlined="quickRange !== 'custom'"
                :color="quickRange === 'custom' ? 'primary' : ''"
                class="rv2-preset mr-3 mb-1"
                v-on="on"
              >
                Custom
              </v-btn>
            </template>
            <v-date-picker v-model="dates" scrollable color="primarygrad" range>
              <v-spacer></v-spacer>
              <v-btn text @click="modal = false">Cancel</v-btn>
              <v-btn text color="primarygrad" @click="onCustomRangeSelected">
                OK
              </v-btn>
            </v-date-picker>
          </v-dialog>
          <v-chip small outlined class="rv2-date-pill mb-1">
            <v-icon x-small left>mdi-calendar</v-icon>
            {{ displayDateRange }}
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <!-- Loader -->
    <v-card v-if="preloader" class="rv2-card mb-4" outlined>
      <v-card-text class="pa-6 text-center">
        <v-progress-circular
          :width="3"
          color="primary"
          indeterminate
        ></v-progress-circular>
        <p class="mt-3 text--secondary mb-0">Loading reports…</p>
      </v-card-text>
    </v-card>

    <!-- KPI strip -->
    <div v-if="!preloader && kpiCards.length > 0" class="rv2-kpi-row mb-4">
      <div v-for="kpi in kpiCards" :key="kpi.key" class="rv2-kpi-cell">
        <div class="rv2-kpi">
          <div class="rv2-kpi-label">{{ kpi.label }}</div>
          <div class="rv2-kpi-value">{{ kpi.value }}</div>
          <div :class="['rv2-kpi-sub', kpi.deltaClass]">{{ kpi.sub }}</div>
        </div>
      </div>
    </div>

    <!-- Charts row -->
    <v-row
      v-if="!preloader && graphData.length > 0"
      dense
      class="rv2-charts-row mb-4"
    >
      <!-- Disposition breakdown -->
      <v-col cols="12" md="8" lg="8">
        <v-card class="rv2-card pa-4 rv2-chart-card rv2-accent-card" outlined>
          <div class="rv2-chart-title mb-3">Disposition breakdown</div>
          <div v-if="dispoBarRows.length === 0" class="rv2-empty">
            No disposition data
          </div>
          <div v-else class="rv2-bars">
            <div
              v-for="row in dispoBarRows"
              :key="row.label"
              class="rv2-bar-row"
            >
              <div class="rv2-bar-label" :title="row.label">
                {{ row.label }}
              </div>
              <div class="rv2-bar-track">
                <div
                  class="rv2-bar-fill"
                  :style="{ width: row.pct + '%', background: row.color }"
                ></div>
              </div>
              <div class="rv2-bar-count">{{ row.count }}</div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Contact ratio -->
      <v-col cols="12" md="4" lg="2">
        <v-card class="rv2-card pa-4 rv2-chart-card rv2-card-fill rv2-accent-card" outlined>
          <div class="rv2-chart-title mb-3">Contact ratio</div>
          <div v-if="contactRatio.total < 10" class="rv2-low-data">
            <div class="rv2-low-data-num">{{ contactRatio.total }}</div>
            <div class="rv2-low-data-text">
              calls so far. Need at least 10 to display the ratio chart.
            </div>
            <div class="rv2-low-data-mini rv2-ring-legend mt-2">
              <div>
                <span class="rv2-dot connected"></span>
                <span class="rv2-legend-label">Connected</span>
                <strong>{{ contactRatio.connected }}</strong>
              </div>
              <div>
                <span class="rv2-dot notconnected"></span>
                <span class="rv2-legend-label">Not connected</span>
                <strong>{{ contactRatio.notConnected }}</strong>
              </div>
            </div>
          </div>
          <div v-else class="rv2-ring-wrap">
            <div class="rv2-ring" :style="ringStyle">
              <div class="rv2-ring-center">{{ contactRatio.pct }}%</div>
            </div>
            <div class="rv2-ring-legend">
              <div>
                <span class="rv2-dot connected"></span>
                <span class="rv2-legend-label">Connected</span>
                <strong>{{ contactRatio.connected }}</strong>
              </div>
              <div>
                <span class="rv2-dot notconnected"></span>
                <span class="rv2-legend-label">Not connected</span>
                <strong>{{ contactRatio.notConnected }}</strong>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Daily trend -->
      <v-col cols="12" md="12" lg="2">
        <v-card class="rv2-card pa-4 rv2-chart-card rv2-card-fill rv2-accent-card" outlined>
          <div class="rv2-chart-title">Daily call trend</div>
          <div class="rv2-chart-sub mb-3">
            Calls per day this period
          </div>
          <div v-if="dailyTrend.length === 0" class="rv2-empty">
            No trend data
          </div>
          <div
            v-else
            class="rv2-trend"
            @mousedown="trendDragStart"
            @mouseleave="trendDragEnd"
            @mouseup="trendDragEnd"
            @mousemove="trendDragMove"
          >
            <div
              v-for="d in dailyTrend"
              :key="d.date"
              class="rv2-trend-col"
              :title="d.title"
            >
              <span class="rv2-trend-count">{{ d.count > 0 ? d.count : "" }}</span>
              <div
                class="rv2-trend-bar-wrap"
              >
                <div
                  class="rv2-trend-bar"
                  :style="{
                    height: d.height + '%',
                    opacity: d.count === 0 ? 0.2 : 1,
                  }"
                ></div>
              </div>
              <div class="rv2-trend-label">{{ d.label }}</div>
            </div>
          </div>
          <div v-if="dailyTrendCaveat" class="rv2-chart-foot mt-2">
            Based on current page data · scroll to load more
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Call log table -->
    <v-card class="rv2-card rv2-accent-card" outlined>
      <v-card-text class="pa-4">
        <v-row dense class="align-center mb-3">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search name, phone or disposition"
              clearable
              outlined
              dense
              hide-details
              class="rv2-search"
              @keyup.enter="query"
              @click:clear="searchReset"
            >
              <template v-slot:append>
                <v-menu offset-y :close-on-content-click="true">
                  <template v-slot:activator="{ on }">
                    <v-btn x-small text class="rv2-scope-chip" v-on="on">
                      {{ scopeLabel }}
                      <v-icon x-small>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-for="f in fieldsList"
                      :key="f.value"
                      @click="setSearchField(f.value)"
                    >
                      <v-list-item-title>{{ f.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-btn height="32" small depressed color="primary" @click="query">
              <v-icon left small>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-menu
              offset-y
              :close-on-content-click="false"
              max-height="500"
              min-width="280"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn height="32" small outlined v-bind="attrs" v-on="on">
                  <v-icon left small>mdi-view-column</v-icon>
                  Columns
                </v-btn>
              </template>
              <v-card class="rv2-col-panel">
                <v-card-text class="pa-3">
                  <div
                    v-for="(group, gname) in headerGroups"
                    :key="gname"
                    class="mb-2"
                  >
                    <div class="rv2-col-group">{{ gname }}</div>
                    <v-checkbox
                      v-for="h in group"
                      :key="h.value"
                      :input-value="isHeaderSelected(h.value)"
                      :label="h.text"
                      :disabled="h.value === 'name' || h.value === 'phone'"
                      hide-details
                      dense
                      color="primary"
                      class="rv2-col-check"
                      @change="toggleHeader(h, $event)"
                    ></v-checkbox>
                  </div>
                  <a class="rv2-reset" @click="resetColumnsToDefault">
                    Reset to defaults
                  </a>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>
          <v-col v-if="!$store.state.user.permissions.disableExportList" cols="auto">
            <v-btn height="32" small outlined @click="exportReport">
              <v-icon left small>mdi-download</v-icon>
              Export
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          ref="reportsDataTable"
          :headers="reportHeaders"
          :items="contactDetails"
          :items-per-page="records"
          hide-default-footer
          :loading="loading"
          :loading-text="loadingText"
          mobile-breakpoint="600"
          class="rv2-table"
        >
          <template v-slot:header.Duration="{ header }">
            <span style="display:inline-flex">
              {{ header.text }}
              <v-tooltip top max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-bind="attrs" v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>
                  Talk time from call answer to hangup, including hold. Green
                  when greater than 0s, red when 0s.
                </span>
              </v-tooltip>
            </span>
          </template>

          <template v-slot:header.TimeSpent="{ header }">
            <span style="display:inline-flex">
              {{ header.text }}
              <v-tooltip top max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-bind="attrs" v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>
                  Total time spent on the contact: ringing + talk + setting
                  disposition.
                </span>
              </v-tooltip>
            </span>
          </template>

          <template v-slot:[`item.dispo`]="{ item }">
            <v-chip
              x-small
              label
              :color="dispoBadge(item.dispo).bg"
              :text-color="dispoBadge(item.dispo).fg"
              class="rv2-dispo-chip"
            >
              {{ formatDispoLabel(item) }}
            </v-chip>
          </template>

          <template v-slot:[`item.Duration`]="{ item }">
            <span v-if="item.type == 'call'">
              <v-chip
                x-small
                label
                :color="durationBadgeColor(item)"
                text-color="white"
              >
                {{ formatDurationCell(item) }}
              </v-chip>
            </span>
          </template>

          <template v-slot:[`item.phone`]="{ item }">
            <div class="rv2-phone-cell">
              <span
                v-if="item.type == 'call' && item.direction == 'incoming'"
                class="rv2-phone-badge rv2-phone-badge--in"
                title="Incoming call"
              >
                <v-icon size="14">mdi-phone-incoming</v-icon>
              </span>
              <span
                v-else-if="item.type == 'call'"
                class="rv2-phone-badge rv2-phone-badge--out"
                title="Outgoing call"
              >
                <v-icon size="14">mdi-phone-outgoing</v-icon>
              </span>
              <span
                v-else-if="item.type == 'sms'"
                class="rv2-phone-badge rv2-phone-badge--sms"
                title="SMS"
              >
                <v-icon size="14">mdi-message-text-outline</v-icon>
              </span>
              <span
                v-else-if="item.type == 'email'"
                class="rv2-phone-badge rv2-phone-badge--email"
                title="Email"
              >
                <v-icon size="14">mdi-email-outline</v-icon>
              </span>
              <span
                v-else-if="item.type == 'link'"
                class="rv2-phone-badge rv2-phone-badge--link"
                title="Link"
              >
                <v-icon size="14">mdi-link-variant</v-icon>
              </span>
              <span
                v-else-if="item.type == 'app'"
                class="rv2-phone-badge rv2-phone-badge--app"
                title="App"
              >
                <v-icon size="14">mdi-apps</v-icon>
              </span>
              <span class="rv2-phone-number">{{ item.phone }}</span>
            </div>
          </template>

          <template v-slot:[`item.secondPhone`]="{ item }">
            <span>{{ item.secondPhone || "-" }}</span>
          </template>

          <template v-slot:[`item.recording.name`]="{ item }">
            <div v-if="item.defaultDialer == 'Exotel'">
              <v-btn
                icon
                small
                class="rv2-play-btn"
                @click="openDialog(item, 'exotel')"
              >
                <v-icon small>mdi-play</v-icon>
              </v-btn>
            </div>
            <div
              v-else-if="
                item.defaultDialer == 'Telecmi' ||
                  item.defaultDialer == 'Telecmi-Linphone'
              "
            >
              <v-btn
                icon
                small
                class="rv2-play-btn"
                @click="openDialogRecordPopup(item, 'telecmi')"
              >
                <v-icon small>mdi-play</v-icon>
              </v-btn>
            </div>
            <div
              v-else-if="
                item.exotel && item.defaultDialer == 'Phone Dialer with Rec'
              "
            >
              <v-btn
                icon
                small
                class="rv2-play-btn"
                @click="download(item.exotel.godialRecordingUrl, 'exotel-rec')"
              >
                <v-icon small>mdi-cloud-download</v-icon>
              </v-btn>
            </div>
            <div v-else-if="item.sipCallId && item.truecallerCallRecording">
              <v-btn
                icon
                small
                class="rv2-play-btn"
                @click="openSipRecording(item)"
              >
                <v-icon small>mdi-play</v-icon>
              </v-btn>
            </div>
            <div v-else-if="item.recording && item.recording.success">
              <v-btn
                icon
                small
                class="rv2-play-btn"
                @click="openDialogRecordPopup(item, 'phone')"
              >
                <v-icon small>mdi-play</v-icon>
              </v-btn>
            </div>
            <div v-else>
              <v-chip x-small label color="#eceff1" text-color="#607d8b">
                Missing
              </v-chip>
            </div>
          </template>
        </v-data-table>

        <!-- Footer -->
        <div class="rv2-footer d-flex align-center flex-wrap mt-3">
          <div class="rv2-records-count grey--text">
            Showing {{ rangeFromTo }} of {{ totalRecords }} record{{
              totalRecords === 1 ? "" : "s"
            }}
          </div>
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <span class="caption mr-2">Rows per page:</span>
            <v-select
              v-model="records"
              :items="rowsPerPageOptions"
              hide-details
              dense
              solo
              flat
              style="max-width:90px"
              @change="onRecordsChange"
            ></v-select>
            <v-btn
              :disabled="page <= 1"
              icon
              small
              class="ml-2"
              @click="prevSet"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="caption">
              Page {{ page }} of {{ pageCount || 1 }}
            </span>
            <v-btn :disabled="page >= pageCount" icon small @click="nextSet">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Loader dialogs (preserved) -->
    <v-dialog v-model="loader" persistent width="300">
      <v-card color="primary white--text" dark>
        <v-card-text style="padding: 18px 25px">
          <h3 style="margin-bottom: 5px; font-weight: 400; font-size: 16px">
            Please Wait...
          </h3>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loader1" persistent width="300">
      <v-card color="primary white--text" dark>
        <v-card-text style="padding: 18px 25px">
          <h3 style="margin-bottom: 5px; font-weight: 400; font-size: 16px">
            Exporting: {{ displayExportProgress }}
          </h3>
          <v-progress-linear
            :value="exportProgress"
            color="white"
            max="130"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogReportsExports" width="900">
      <v-card>
        <v-card-title class="primarygrad">
          Export Report
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogReportsExports = false">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mt-3">
          <v-row class="pa-0 ma-0">
            <v-col xs12 sm12 md12 style="text-align: center; padding: 5px">
              <v-dialog
                ref="dialog"
                v-model="modal"
                :return-value.sync="dates"
                persistent
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateRange"
                    label="Date Range"
                    prepend-inner-icon="event"
                    class="my-2"
                    hide-details="true"
                    solo
                    flat
                    outlined
                    background-color="inverse"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dates"
                  scrollable
                  color="primarygrad"
                  range
                >
                  <v-spacer></v-spacer>
                  <v-btn text @click="modal = false">Cancel</v-btn>
                  <v-btn
                    text
                    color="primarygrad"
                    @click="$refs.dialog.save(dates)"
                  >
                    OK
                  </v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="red" @click="dialogReportsExports = false">
            Cancel
          </v-btn>
          <v-btn depressed color="primarygrad" @click="saveExportReports">
            Export
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" multi-line :color="color">
      <h3 style="font-weight: 400; font-size: 13px; text-transform: uppercase">
        {{ snackbarText }}
      </h3>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <record-popup ref="recordPopup"></record-popup>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
const _ = require("lodash");
import Papa from "papaparse";
import Blob from "blob";
import fb from "../js/firebase";
import DoughnutChart from "../js/doughnut.js";
import BarChart from "../js/bar.js";
import PieChart from "../js/pie.js";
import moment from "moment-timezone";
import * as XLSX from "xlsx/xlsx.mjs";
import recordPopup from "../components/recordPopup.vue";

export default {
  components: {
    DoughnutChart,
    BarChart,
    PieChart,
    recordPopup,
  },
  data() {
    return {
      deletedListIds: [],
      showHeader: false,
      exportProgress: 0,
      currentBatch: 0,
      totalBatches: 0,
      dialogReportsExports: false,
      telecmiIntegration: false,
      snackbar: false,
      snackbarText: "",
      color: "green",
      defaultDialer: "",
      audioDialog: false,
      selectedAudio: null,
      selectedLog: null,
      isPlaying: false,
      playbackTime: 0,
      selectedCallDirection: "all",
      callDirections: [
        { text: "All", value: "all" },
        { text: "Incoming", value: "incoming" },
        { text: "Outgoing", value: "outgoing" },
      ],
      // teams: [],
      labelShown: true,
      totalCallCount: 0,
      totalCallDuration: "",
      barChartData: {},
      pieChartData: {},
      statistics: [],
      datacollection: {},
      // pagination structure
      loader: false,
      loader1: false,
      recordsPerPage: [5, 15, 30, 60, 120, 500],
      page: 1,
      pageCount: 0,
      totalRecords: 0,
      records: 25,
      selectedUser: [],
      selectedList: [],
      lists: [],
      templates: [],
      preloader: false,
      search: "",
      contactDetails: [],
      loadChart: false,
      allUsers: [],
      allHeaders: [
        {
          text: "Name",
          align: "left",
          sortable: false,
          value: "name",
        },
        { text: "Phone", align: "left", sortable: false, value: "phone" },
        {
          text: "Secondary Phone",
          align: "left",
          sortable: false,
          value: "secondPhone",
        },
        {
          text: "Type",
          align: "left",
          sortable: false,
          value: "type",
        },
        {
          text: "Date",
          align: "left",
          sortable: false,
          value: "date",
        },
        {
          text: "Time",
          align: "left",
          sortable: false,
          value: "time",
        },
        {
          text: "Duration",
          align: "left",
          sortable: false,
          value: "Duration",
        },
        {
          text: "Time Spent",
          align: "left",
          sortable: false,
          value: "TimeSpent",
        },
        {
          text: "Dialer",
          align: "left",
          sortable: false,
          value: "defaultDialer",
        },
        {
          text: "Called/Received By",
          align: "left",
          sortable: false,
          value: "accounts.name",
        },
        {
          text: "Disposition",
          align: "left",
          sortable: false,
          value: "dispo",
        },
        {
          text: "Remarks",
          align: "left",
          sortable: false,
          value: "remarks",
        },
        {
          text: "Notes",
          align: "left",
          sortable: false,
          value: "note",
        },
        {
          text: "List",
          align: "left",
          sortable: false,
          value: "list.name",
        },
        {
          text: "Team",
          align: "left",
          sortable: false,
          value: "list.teams.name",
        },
        {
          text: "Template",
          align: "left",
          sortable: false,
          value: "template.title",
        },
        {
          text: "Recording",
          align: "left",
          sortable: false,
          value: "recording.name",
        },
        {
          text: "Call Status",
          align: "left",
          sortable: true,
          value: "callStatus",
        },
      ],
      modal: false,
      dates: [],
      dispos: [],
      selectedDispos: [],
      graphData: [],
      loading: false,
      loadingText: "",
      searchField: "name",
      fieldsList: [
        { name: "Name", value: "name" },
        { name: "Phone", value: "phone" },
        { name: "Secondary Phone", value: "secondPhone" },
        { name: "Remarks", value: "remarks" },
        { name: "Note", value: "note" },
      ],

      /* ---- ReportsV2 additions ---- */
      // Active quick-range preset. 'last7' is the default for parity with
      // the original Master report (which loaded the last 7 days).
      quickRange: "last7",
      quickRangePresets: [
        { key: "today", label: "Today" },
        { key: "yesterday", label: "Yesterday" },
        { key: "last7", label: "Last 7 days" },
        { key: "thisWeek", label: "This week" },
        { key: "lastWeek", label: "Last week" },
        { key: "thisMonth", label: "This month" },
      ],
      // Rows-per-page options for the V2 footer
      rowsPerPageOptions: [25, 50, 100],
      // Aggregated totals for the previous period (for KPI deltas).
      // Same shape as current period totals.
      prevPeriodTotals: {
        totalCalls: 0,
        totalTalkSecs: 0,
        connectedCalls: 0,
        topDispo: null,
      },
      // Default header set used by "Reset to defaults" in column picker.
      // Hides Template + Called/Received By + Call Status by default.
      defaultHeaderValues: [
        "name",
        "phone",
        "date",
        "time",
        "Duration",
        "TimeSpent",
        "defaultDialer",
        "dispo",
        "remarks",
        "note",
        "list.name",
        "recording.name",
      ],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "user", "fieldList", "teams", "companyDetails"]),
    reportHeaders: {
      get: function() {
        return this.$store.getters.reportHeaders;
      },
      set: function(newValue) {
        this.$store.commit("setReportHeaders", newValue);
      },
    },
    dateRange() {
      return this.dates.join(" - ");
    },
    dateRange1() {
      return this.dates.join(" - ");
    },
    dispoList() {
      return this.$store.getters.dispoList;
    },
    noReports() {
      return require("../assets/noReports.png");
    },
    selectedTeam: {
      get(v) {
        return this.$store.getters.selectedTeam;
      },
      set(v) {
        this.$store.commit("setSelectedTeam", v);
      },
    },
    calcDispo() {
      return _.without(
        _.map(this.dispos, (d) => {
          if (
            d.title == "SMS" ||
            d.title == "LINK" ||
            d.title == "EMAIL" ||
            d.title == "APP" ||
            d.title == "NEW"
          ) {
            //do not pass these values
          } else {
            return d;
          }
        }),
        undefined,
      );
    },
    displayExportProgress() {
      if (this.exportProgress <= 100) {
        return `Batch ${this.currentBatch}/${this.totalBatches} ( ${this.exportProgress}% )`;
      } else {
        return `Merging files: ${this.exportProgress - 100}%`;
      }
    },
    paginate() {
      return this.$store.getters.paginate;
    },

    /* ===== ReportsV2 computeds ===== */
    teamName() {
      const t = this.selectedTeam;
      if (!t) return "";
      if (typeof t === "string") {
        const found = _.find(this.teams, (x) => x.id === t);
        return found ? found.name : "";
      }
      return t.name || "";
    },
    scopeLabel() {
      const f = _.find(this.fieldsList, (x) => x.value === this.searchField);
      return f ? f.name : "Name";
    },
    headerGroups() {
      // Grouped column toggle (Contact info / Call details / Outcome / Meta).
      return {
        "Contact info": _.filter(this.allHeaders, (h) =>
          ["name", "phone", "secondPhone", "type"].includes(h.value),
        ),
        "Call details": _.filter(this.allHeaders, (h) =>
          ["date", "time", "Duration", "TimeSpent", "defaultDialer"].includes(
            h.value,
          ),
        ),
        Outcome: _.filter(this.allHeaders, (h) =>
          ["dispo", "remarks", "note", "recording.name", "callStatus"].includes(
            h.value,
          ),
        ),
        Meta: _.filter(this.allHeaders, (h) =>
          [
            "list.name",
            "list.teams.name",
            "template.title",
            "accounts.name",
          ].includes(h.value),
        ),
      };
    },
    // Aggregated totals for the current period derived from `statistics`.
    currentPeriodTotals() {
      let totalCalls = 0;
      let totalTalkSecs = 0;
      let connectedCalls = 0;
      _.each(this.statistics || [], (s) => {
        totalCalls += s.count || 0;
        // statistics duration is divided by 1000 inside fillBarData; keep
        // robust: use raw `duration` (ms) if present, else fall back.
        const raw = typeof s.duration === "number" ? s.duration : 0;
        // After fillBarData, duration is in minutes; before, in ms. Detect.
        if (raw > 10000) {
          totalTalkSecs += raw / 1000; // ms -> seconds
        } else {
          totalTalkSecs += raw * 60; // minutes -> seconds (post-fill)
        }
      });
      // Connected = sum over graphData of non-"Not Picked Up"/"Foreign"
      // unconnected dispositions; fall back to counting graphData entries.
      const unconnected = [
        "NOT PICKED UP",
        "NOT-PICKED-UP",
        "FOREIGN",
        "BUSY",
        "UNREACHABLE",
      ];
      _.each(this.graphData || [], (g) => {
        const id = String(g._id || "").toUpperCase();
        if (!unconnected.includes(id)) connectedCalls += g.count || 0;
      });
      const topDispoEntry = _.maxBy(this.graphData || [], "count");
      return {
        totalCalls,
        totalTalkSecs,
        connectedCalls,
        topDispo: topDispoEntry || null,
      };
    },
    kpiCards() {
      const cur = this.currentPeriodTotals;
      const prev = this.prevPeriodTotals;
      if (!cur || cur.totalCalls === 0) return [];

      const avgSec =
        cur.totalCalls > 0 ? cur.totalTalkSecs / cur.totalCalls : 0;
      const connRate =
        cur.totalCalls > 0
          ? Math.round((cur.connectedCalls / cur.totalCalls) * 100)
          : 0;
      const prevAvg =
        prev.totalCalls > 0 ? prev.totalTalkSecs / prev.totalCalls : 0;
      const prevConn =
        prev.totalCalls > 0
          ? Math.round((prev.connectedCalls / prev.totalCalls) * 100)
          : 0;

      const cards = [
        {
          key: "totalCalls",
          label: "Total calls",
          value: cur.totalCalls.toLocaleString(),
          ...this.deltaInfo(cur.totalCalls, prev.totalCalls, "vs last period"),
        },
        {
          key: "talkTime",
          label: "Total talk time",
          value: this.formatHm(cur.totalTalkSecs),
          ...this.deltaInfo(
            cur.totalTalkSecs,
            prev.totalTalkSecs,
            "vs last period",
          ),
        },
        {
          key: "connRate",
          label: "Connection rate",
          value: `${connRate}%`,
          ...this.deltaInfo(connRate, prevConn, "vs last period", true),
        },
        {
          key: "avgDur",
          label: "Avg call duration",
          value: this.formatMs(avgSec),
          ...this.deltaInfo(avgSec, prevAvg, "vs last period"),
        },
      ];
      if (cur.topDispo) {
        const pct =
          cur.totalCalls > 0
            ? Math.round((cur.topDispo.count / cur.totalCalls) * 100)
            : 0;
        cards.push({
          key: "topDispo",
          label: "Top disposition",
          value: this.toTitle(cur.topDispo._id),
          sub: `${cur.topDispo.count} calls (${pct}%)`,
          deltaClass: "neutral",
        });
      }
      return cards;
    },
    dispoBarRows() {
      const rows = _.orderBy(
        _.map(this.graphData || [], (g) => ({
          label: this.toTitle(g._id),
          rawKey: String(g._id || "").toUpperCase(),
          count: g.count || 0,
        })),
        ["count"],
        ["desc"],
      );
      if (rows.length === 0) return [];

      // Keep top 7 + aggregate the remainder into "Others"
      let top = rows.slice(0, 7);
      let others = rows.slice(7);
      if (others.length > 0) {
        const othersCount = _.sumBy(others, "count");
        let existingOthers = top.find(r => r.rawKey === "OTHERS");
        if (existingOthers) {
          existingOthers.count += othersCount;
        } else {
          top.push({ label: "Others", rawKey: "OTHERS", count: othersCount });
        }
      }
      const max = _.maxBy(top, "count").count || 1;

      // Each disposition has its own configured `hex` color in dispoList —
      // fall back to neutral palette only when not found.
      const positive = [
        "INTERESTED",
        "DEMO SCHEDULED",
        "DEMO-SCHEDULED",
        "SUPPORT",
      ];
      const list = this.dispoList || [];
      return top.map((r) => {
        let color;
        if (r.rawKey === "OTHERS") {
          color = "#b0bec5";
        } else {
          const match = _.find(
            list,
            (d) => String(d.title || "").toUpperCase() === r.rawKey,
          );
          color =
            (match && match.hex) ||
            (positive.includes(r.rawKey) ? "#2ecc71" : "#7c4dff");
        }
        return {
          ...r,
          pct: Math.round((r.count / max) * 100),
          color,
        };
      });
    },
    contactRatio() {
      const cur = this.currentPeriodTotals;
      const connected = cur.connectedCalls;
      const total = cur.totalCalls;
      const notConnected = Math.max(total - connected, 0);
      const pct = total > 0 ? Math.round((connected / total) * 100) : 0;
      return { connected, notConnected, total, pct };
    },
    ringStyle() {
      // Conic-gradient ring driven by contactRatio.pct
      const p = this.contactRatio.pct;
      return {
        background: `conic-gradient(#2ecc71 0% ${p}%, #e0e0e0 ${p}% 100%)`,
      };
    },
    dailyTrend() {
      // Derived client-side from the currently loaded `contactDetails`.
      // For a full-period trend, an aggregation endpoint would be ideal —
      // tracked as a follow-up. For now this reflects the visible page.
      if (!this.dates || this.dates.length === 0) return [];
      const start = moment(this.dates[0]);
      const end = this.dates[1] ? moment(this.dates[1]) : moment(this.dates[0]);
      const lo = start.isBefore(end) ? start : end;
      const hi = start.isBefore(end) ? end : start;
      const days = hi.diff(lo, "days") + 1;
      if (days <= 0 || days > 31) return [];

      const buckets = {};
      _.each(this.contactDetails || [], (r) => {
        if (r.type !== "call" || !r.calledOn) return;
        const k = moment(r.calledOn).format("YYYY-MM-DD");
        buckets[k] = (buckets[k] || 0) + 1;
      });
      const series = [];
      const dayInitial = ["S", "M", "T", "W", "T", "F", "S"];
      for (let i = 0; i < days; i++) {
        const d = lo
          .clone()
          .add(i, "days")
          .format("YYYY-MM-DD");
        series.push({
          date: d,
          label: dayInitial[moment(d).day()],
          title: `${moment(d).format("ddd, MMM D")} - ${buckets[d] || 0} calls`,
          count: buckets[d] || 0,
        });
      }
      const max = _.maxBy(series, "count");
      const m = max && max.count > 0 ? max.count : 1;
      return series.map((s) => ({ ...s, height: (s.count / m) * 100 }));
    },
    dailyTrendCaveat() {
      // Show a "page sample" note when we only have partial data loaded.
      return (
        (this.contactDetails || []).length > 0 &&
        this.totalRecords > (this.contactDetails || []).length
      );
    },
    displayDateRange() {
      if (!this.dates || this.dates.length === 0) return "—";
      const a = moment(this.dates[0]).format("YYYY-MM-DD");
      const b = moment(this.dates[1] || this.dates[0]).format("YYYY-MM-DD");
      return `${a} — ${b}`;
    },
    shownCount() {
      return (this.contactDetails || []).length;
    },
    rangeFromTo() {
      // "1–25" style range that reflects the actual slice loaded.
      const total = this.totalRecords || 0;
      if (total === 0) return "0";
      const start = (this.page - 1) * this.records + 1;
      const end = Math.min(
        start + (this.contactDetails || []).length - 1,
        total,
      );
      return `${start}–${end}`;
    },
  },
  watch: {
    async selectedTeam() {
      try {
        await this.refresh(); // get accounts and lists and dispos
        await this.load(); // get the logs and load charts
      } catch (e) {
        console.log(e);
      }
    },
  },
  async mounted() {
    // Graphs
    // this.fillBarData();
    // this.fillData();
    this.$nextTick(() => this.attachColumnResizers());
  },
  updated() {
    // Re-attach resize grips whenever the table re-renders (headers toggled,
    // page changed, etc.). attachColumnResizers is idempotent.
    this.$nextTick(() => this.attachColumnResizers());
  },
  async created() {
    // Check if header was dismissed
    this.showHeader = localStorage.getItem("reportsHeaderDismissed") !== "true";
    try {
      // Paginate
      var routeName = this.$router.currentRoute.name
        .toString()
        .trim()
        .replace(/ /g, "")
        .toLowerCase();
      if (this.paginate[routeName]) {
        this.page = this.paginate[routeName].page;
        (this.pageCount = this.paginate[routeName].pageCount),
          (this.totalRecords = this.paginate[routeName].totalRecords),
          (this.records = this.paginate[routeName].records);
      }

      // Default to last-7-days when no saved dates (parity with old Master).
      if (!this.dates || this.dates.length === 0) {
        const tz =
          (this.companyDetails && this.companyDetails.timezone) ||
          "Asia/Kolkata";
        const now = moment.tz(tz);
        this.dates = [
          now
            .clone()
            .subtract(6, "days")
            .startOf("day")
            .format("YYYY-MM-DD"),
          now
            .clone()
            .endOf("day")
            .format("YYYY-MM-DD"),
        ];
        this.quickRange = "last7";
      }

      await this.refresh(); // get accounts and lists and dispos
      await this.fetchDeletedListIds();
      await this.load(); // get the logs and load charts
      this.getTelecmiDetails();
    } catch (e) {
      console.log(e);
    }
  },
  methods: {
    // Scroll table horizontally
    scrollReportsTable(direction) {
      const dataTable = this.$refs.reportsDataTable;
      const tableWrapper =
        dataTable &&
        dataTable.$el &&
        dataTable.$el.querySelector(".v-data-table__wrapper");
      if (tableWrapper) {
        const scrollAmount = 200;
        if (direction === "left") {
          tableWrapper.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        } else {
          tableWrapper.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
      }
    },
    isHeaderSelected(value) {
      return this.reportHeaders.some((h) => h.value === value);
    },
    toggleHeader(item, checked) {
      let headers = [...this.reportHeaders];
      if (checked) {
        // Add the header if not already present
        if (!headers.some((h) => h.value === item.value)) {
          headers.push(item);
        }
      } else {
        // Remove the header
        headers = headers.filter((h) => h.value !== item.value);
      }
      this.reportHeaders = headers;
    },
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("reportsHeaderDismissed", "true");
    },
    goToClassicReports() {
      this.$router.push({ path: "/reports" });
    },
    async getTelecmiDetails() {
      var resp = await this.$http.get(
        `${this.ENDPOINT}/integrations?filter[where][companyId]=${this.companyDetails.id}&filter[where][type]=telecmi`,
      );

      if (resp.body.length > 0) this.telecmiIntegration = resp.body[0];
    },
    openDialog(item, dialingOpt) {
      console.log("item 112233", item);

      console.log("dialingOpt 11223344", dialingOpt);

      if (dialingOpt == "exotel") {
        (this.selectedAudio = null), (this.selectedLog = null);
        if (typeof item.serverResponse.RecordingUrl != "undefined") {
          var audioExotel = new Audio(`${item.serverResponse.RecordingUrl}`);
          this.selectedAudio = audioExotel;

          console.log("item 11111111111111111111111111111", item);

          this.selectedLog = item;
          this.audioDialog = true;
        }
      } else {
        (this.selectedAudio = null), (this.selectedLog = null);
        if (typeof item.recording != "undefined") {
          var audioPhone = new Audio(
            `${this.$store.state.ENDPOINT}/storages/get/${item.recording.name}?access_token=${this.$store.getters.user.token}`,
          );
          this.selectedAudio = audioPhone;
          this.selectedLog = item;
          this.audioDialog = true;
        }
      }
    },

    closeDialog() {
      this.selectedAudio.pause();
      // reset item var
      (this.isPlaying = false),
        (this.playbackTime = 0),
        (this.selectedAudio = null),
        (this.selectedLog = null),
        (this.audioDialog = false);
    },

    openDialogRecordPopup(item, dialingOpt) {
      console.log("item in open dialog", item);

      console.log("item in dialingOpt", dialingOpt);

      if (dialingOpt == "exotel") {
        (this.selectedAudio = null), (this.selectedLog = null);
        if (typeof item.serverResponse.RecordingUrl != "undefined") {
          var audioExotel = new Audio(`${item.serverResponse.RecordingUrl}`);
          this.selectedAudio = audioExotel;

          this.selectedLog = item;
          this.audioDialog = true;
        }
      }
      // else if (dialingOpt == "exotel-rec") {
      //   (this.selectedAudio = null), (this.selectedLog = null);
      //   if (typeof item.exotel.recordingUrl != "undefined") {
      //     var audioExotel2 = new Audio(`${item.exotel.recordingUrl}`);
      //     this.selectedAudio = audioExotel2;

      //     this.selectedLog = item;
      //     this.audioDialog = true;
      //   }
      // }
      else {
        console.log("this.$refs", this.$refs);
        this.$refs.recordPopup.openDialog(item, dialingOpt);
      }
    },

    closeDialogRecordPopup() {
      this.$refs.recordPopup.closeDialog(false);
    },

    async openSipRecording(item) {
      try {
        // Debug: log the entire item to see what fields are available
        console.log("🔍 Full item object:", JSON.stringify(item, null, 2));
        console.log("📞 SIP Call ID (sipCallId):", item.sipCallId);

        // Check if we already have the recording URL in truecallerCallRecording
        if (item.truecallerCallRecording && item.truecallerCallRecording.url) {
          console.log(
            "✅ Using existing recording URL from truecallerCallRecording",
          );

          // Add sipCallRecording to item for recordPopup
          item.sipCallRecording = {
            mergedUrl: item.truecallerCallRecording.url,
          };

          // Now open the dialog with the loaded data
          this.$refs.recordPopup.openDialog(item, "sip");
          return;
        }

        // Fallback: Try to fetch by sipCallId if truecallerCallRecording doesn't have URL
        const callId = item.sipCallId;

        if (!callId) {
          console.error("❌ No sipCallId found in item!");
          this.snackbarText = "Call ID not found for this recording.";
          this.color = "error";
          this.snackbar = true;
          return;
        }

        console.log("✅ Fetching recording by Call-ID:", callId);

        // Fetch recording by callId or uacCallId (for B2BUA scenarios)
        const response = await this.$http.get(
          `${
            this.$store.state.ENDPOINT
          }/sip-call-recordings?filter[where][or][0][callId]=${encodeURIComponent(
            callId,
          )}&filter[where][or][1][uacCallId]=${encodeURIComponent(
            callId,
          )}&access_token=${this.$store.getters.user.token}`,
        );

        if (response.data && response.data.length > 0) {
          const recording = response.data[0];
          console.log("✅ Found SIP recording:", recording);

          // Add sipCallRecording to item for recordPopup
          item.sipCallRecording = {
            mergedUrl: recording.gcsUrl || recording.mergedUrl,
            legAUrl: recording.legAUrl,
            legBUrl: recording.legBUrl,
          };

          // Now open the dialog with the loaded data
          this.$refs.recordPopup.openDialog(item, "sip");
        } else {
          // Recording not found or still processing
          this.snackbarText =
            "Call recording is still processing or not available yet.";
          this.color = "warning";
          this.snackbar = true;
        }
      } catch (error) {
        console.error("Error fetching SIP recording:", error);
        this.snackbarText = "Failed to load call recording.";
        this.color = "error";
        this.snackbar = true;
      }
    },

    download(src, dialingOpt) {
      if (src != "") {
        if (dialingOpt == "exotel") {
          var a = document.createElement("a");

          a.href = src;
          a.setAttribute("download", src.toString());
          a.target = "_blank";
          a.click();
        } else if (dialingOpt == "exotel-rec") {
          var url3 = src;

          console.log("url3", url3);

          var d3 = document.createElement("a");

          d3.href = url3;
          d3.setAttribute("download", url3.toString());
          d3.target = "_blank";
          d3.click();
        } else {
          var au = document.createElement("a");

          au.href = `${this.$store.state.ENDPOINT}/storages/get/${src}?access_token=${this.$store.getters.user.token}`;
          au.setAttribute("download", src.toString());
          au.target = "_blank";
          au.click();
        }
      }
    },
    audio(what) {
      const self = this;
      var audio = self.selectedAudio;
      if (typeof audio != "undefined") {
        switch (what) {
          case "start":
            self.isPlaying = true;
            audio.play();

            break;

          case "pause":
            // access audio from ongoing selected track
            audio.pause();
            // reset item var
            self.isPlaying = false;
            // self.playbackTime = 0;
            break;
        }

        audio.ontimeupdate = function(e) {
          self.playbackTime = (audio.currentTime / audio.duration) * 100;
          if (self.playbackTime >= 100 || isNaN(self.playbackTime)) {
            self.isPlaying = false;
            self.playbackTime = 0;
          }
        };
      }
    },

    /** SEARCH */
    async query() {
      try {
        if (this.search != "" && this.searchField != "") {
          this.loading = true;
          this.loadingText = "Loading... Please wait";
          this.page = 1;
        }
        this.fetchCount();
        this.contactDetails = await this.fetchLogs(
          this.records,
          this.records * this.page - this.records,
        );
      } catch (e) {
        // error
      }
    },
    searchReset() {
      this.searchField = "name";
      this.search = "";
      this.query();
    },

    /** PAGINATION */
    nextSet() {
      this.page++;
      this.pagination();
    },
    prevSet() {
      this.page--;
      this.pagination();
    },
    async pagination() {
      this.pageCount = Math.ceil(this.totalRecords / this.records);
      if (this.page > this.pageCount) {
        this.page = 1; //set default
      }

      this.contactDetails = await this.fetchLogs(
        this.records,
        this.records * this.page - this.records,
      );

      // Save default pagination of seperate pages
      this.paginate[
        this.$router.currentRoute.name
          .toString()
          .trim()
          .replace(/ /g, "")
          .toLowerCase()
      ] = {
        page: this.page,
        pageCount: this.pageCount,
        records: this.records,
        totalRecords: this.totalRecords,
      };
      this.$store.commit("setPagination", this.paginate);
      return;
    },

    /** INIT CODE */
    // Load the details

    async load() {
      this.page = 1;
      this.contactDetails = [];
      this.statistics = [];
      this.graphData = [];

      if (this.page > this.pageCount) {
        this.page = 1;
      }
      // date range limit
      var start = moment(this.dates[0]);
      var end = this.dates[1] == undefined ? start : moment(this.dates[1]);
      if (start > end) {
        var temp = start;
        start = end;
        end = temp;
      }
      var diff = end.diff(start, "days") + 1;

      // if range is greater than 30 days
      if (diff > 31) {
        this.loading = false;
        this.loadingText = "";
        this.snackbar = true;
        this.snackbarText = "Please select a range of 30 days";
        this.color = "#cc9900";
        return;
      }

      // // if select more than 5 lists
      if (this.selectedList.length > 5) {
        this.loading = false;
        this.loadingText = "";
        this.snackbar = true;
        this.snackbarText =
          " you can only select a maximum of five lists at a time. Please deselect some lists before continuing.";
        this.color = "#cc9900";
        return;
      }

      this.fetchGraphReports(); // graphical report
      this.fetchCount(); // Total log count
      this.contactDetails = await this.fetchLogs(
        this.records,
        this.records * this.page - this.records,
      ); // logs by limit and skip

      // Set field data
      if (this.fieldsList.length > 0 && this.searchField == "") {
        this.searchField = this.fieldsList[0].value;
      }
    },

    // load info for filter
    async refresh() {
      try {
        await this.fetchLists();
        await this.fetchAccounts();
        await this.fetchDispos();
        await this.fetchTemplates();
      } catch (e) {
        // error
      }
    },

    /** DELETE APIIS */
    deleteItem(item) {
      const self = this;
      self.$http.delete(self.ENDPOINT + "/logs/" + item.id).then(function() {
        self.fetchLogs();
      });
    },

    /** GET ARRAY OF list, user ids */
    getListIds() {
      var lists = [];
      if (_.includes(this.selectedList, "allLists")) {
        // lists = [];
        lists = _.map(this.lists, "id");
        if (this.deletedListIds && this.deletedListIds.length > 0) {
          lists = lists.concat(this.deletedListIds);
        }
      } else {
        lists = this.selectedList;
      }
      lists = _.without(lists, "allLists");
      return lists;
    },
    getTemplateIds() {
      var templates = [];
      templates = _.map(this.templates, "id");
      return templates;
    },
    getUserIds() {
      var users = [];

      if (
        _.includes(this.selectedUser, "allMembers") &&
        this.$store.getters.user.role.toLowerCase() == "manager"
      ) {
        users = [];
      } else if (
        _.includes(this.selectedUser, "allMembers") &&
        this.$store.getters.user.role.toLowerCase() == "teammanager"
      ) {
        users = _.map(this.allUsers, "id");
      } else {
        users = this.selectedUser;
      }
      users = _.without(users, "allMembers");
      return users;
    },

    allListReports() {
      this.dialogReportsExports = true;
    },

    async saveExportReports() {
      var self = this;

      // date range limit
      var start = moment(this.dates[0]);
      var end = this.dates[1] == undefined ? start : moment(this.dates[1]);
      if (start > end) {
        var temp = start;
        start = end;
        end = temp;
      }
      var diff = end.diff(start, "days") + 1;

      // if range is greater than 30 days
      if (diff > 31) {
        this.loading = false;
        this.loadingText = "";
        this.snackbar = true;
        this.snackbarText = "Please select a range of 30 days";
        this.color = "#cc9900";
        return;
      }

      if (this.dates.length < 1) {
        this.dates = [
          moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
      }

      var lists = _.map(this.lists, "id");

      lists = _.without(lists, "allLists");
      if (this.deletedListIds && this.deletedListIds.length > 0) {
        lists = lists.concat(this.deletedListIds);
      }

      const searchParamsForCount = {
        search: "",
        field: "",
        range: this.dates,
        lists: lists,
        users: [],
        dispos: [],
        count: true,
      };

      const responseCount = await this.$http.post(
        this.ENDPOINT + "/logs/byFilter",
        searchParamsForCount,
      );

      console.log("response of count", responseCount);

      var dataResultFinal = [];

      await Promise.all(
        lists.map(async (c) => {
          var searchParams = {
            search: "",
            field: "",
            range: this.dates,
            lists: [c],
            users: [],
            dispos: [],
            count: false,
          };

          var response = await this.$http.post(
            this.ENDPOINT + "/logs/byFilter",
            searchParams,
          );

          console.log("response body", response.body.data);

          if (response.body.data.length > 0) {
            dataResultFinal.push(...response.body.data);
          }
        }),
      );

      console.log("data", dataResultFinal);

      // const searchParams = {
      //   search: "",
      //   field: "",
      //   range: this.dates,
      //   lists: lists,
      //   users: [],
      //   dispos: [],
      //   limit: 5,
      //   skip: 0,
      //   count: false,
      // };
      // const data = [];
      // const result = await this.fetchData(searchParams, data, responseCount);
      // console.log(result);

      // if (typeof result != "undefined") {
      //   data.push(result);
      // }

      var dataResult = _.map(dataResultFinal, (r) => {
        // accounts
        var account = _.find(this.allUsers, (user) => {
          return r.accountsId == user.id;
        });
        if (account) {
          r.accounts = account;
        }

        //lists
        var list = _.find(this.lists, (list) => {
          return r.listId == list.id;
        });
        if (list) {
          r.list = list;
        }

        //templates
        var template = _.find(this.templates, (template) => {
          return r.templateId == template.id;
        });
        if (template) {
          r.template = template;
        }

        return r;
      });

      console.log("dataResult", dataResult);

      var exportData = [];

      exportData = _.orderBy(dataResult, "time", "desc");

      console.log("exportData", exportData);

      if (exportData.length < 1) {
        this.loader = false;
        this.$swal({ tyep: "warning", text: "No records to export" });
        return;
      }

      exportData = _.map(exportData, (d) => {
        var y = {
          Phone: d.phone.toString(),
        };

        if (typeof d.name != "undefined") {
          y.Name = d.name;
        } else {
          y.Name = "";
        }

        if (typeof d.companyName != "undefined") {
          y.Company = d.companyName;
        } else {
          y.Company = "";
        }

        if (d.calledOn) {
          y.Date = this.$moment(d.calledOn).format("YYYY-MM-DD");
          y.Time = this.$moment(d.calledOn).format("hh:mm a");
        } else {
          y.Date = "";
          y.Time = "";
        }

        if (d.leadScore) {
          y.Leadscore = d.leadScore;
        } else {
          y.Leadscore = "";
        }

        if (d.remarks) {
          y.Remarks = d.remarks;
        } else {
          y.Remarks = "";
        }

        if (d.extra) {
          y.Extra = d.extra;
        } else {
          y.Extra = "";
        }

        if (d.accounts && d.accounts.name) {
          y.CalledBy = d.accounts.name;
        } else {
          y.CalledBy = "";
        }

        if (d.direction) {
          y.Direction = d.direction;
        } else {
          y.Direction = "outgoing";
        }

        if (typeof d.list == "undefined" || d.list.name == "") {
          y.List = "";
        } else {
          y.List = d.list.name;
        }

        if (d.dispo) {
          y.Disposition = d.dispo;
        } else {
          y.Disposition = "";
        }

        if (d.callbackOn) {
          y.CallbackOn = this.$moment(d.callbackOn).format(
            "DD-MM-YYYY, h:mm a",
          );
        } else {
          y.CallbackOn = "";
        }

        if (d.email != "") {
          y.Email = d.email;
        } else {
          y.Email = "";
        }

        // Default Dialer
        if (d.type == "call" && typeof d.defaultDialer == "undefined") {
          y.defaultDialer = "Phone Dialer";
        } else {
          y.defaultDialer = d.defaultDialer;
        }

        if (d.type == "link") {
          y.defaultDialer = "";
        }

        if (d.list && d.list.teams) {
          y.Team = d.list.teams.name;
        } else {
          y.Team = "";
        }

        if (d.note) {
          y.Notes = d.note;
        } else {
          y.Notes = "";
        }

        if (d.duration) {
          y["duration (sec)"] = Number(d.duration / 1000).toFixed(2);
        }

        if (d.type == "call") {
          if (parseInt(d.Duration) > 0) {
            y.CallStatus = "Connected";
          } else {
            y.CallStatus = "Not Connected";
          }
        } else {
          y.CallStatus = "";
        }

        if (d.timeSpentOnCall) {
          y["TimeSpent (sec)"] = Number(d.timeSpentOnCall / 1000).toFixed(2);
        }

        var statusArray = [];

        if (d.emailSent) {
          statusArray.push("Email");
        }

        if (d.smsSent) {
          statusArray.push("SMS");
        }

        if (d.whatsappSent) {
          statusArray.push("Whatsapp");
        }
        if (d.linkSent) {
          statusArray.push("Link");
        }

        if (d.appSent) {
          statusArray.push("App");
        }

        if (d.called) {
          statusArray.push("Call");
        }

        if (typeof statusArray !== "undefined" && statusArray.length > 0) {
          y.Status = statusArray.join();
        } else {
          y.Status = "";
        }

        if (typeof d.template == "undefined" || d.template.title == "") {
          y.Template = "";
        } else {
          y.Template = d.template.title;
        }

        if (
          typeof d.truecallerCallRecording == "undefined" ||
          d.truecallerCallRecording.name == ""
        ) {
          y.CallRecordingPath = "";
        } else {
          if (typeof d.truecallerCallRecording.name != "undefined") {
            y.CallRecordingPath = `${this.$store.state.ENDPOINT}/storages/get/${d.truecallerCallRecording.name}?access_token=${this.$store.getters.user.token}`;
          } else {
            y.CallRecordingPath = "";
          }
        }

        if (
          typeof d.telecmi != "undefined" &&
          (d.defaultDialer == "Telecmi" ||
            d.defaultDialer == "Telecmi-Linphone")
        ) {
          if (typeof d.telecmi.filename != "undefined") {
            var url = `https://rest.telecmi.com/v2/play?appid=${self.telecmiIntegration.config.appid}&secret=${self.telecmiIntegration.config.secret}&file=${d.telecmi.filename}`;
            y.CallRecordingPath = url;
          } else {
            y.CallRecordingPath = "";
          }
        }

        if (
          typeof d.exotel != "undefined" &&
          d.defaultDialer == "Phone Dialer with Rec"
        ) {
          if (typeof d.exotel.recordingUrl != "undefined") {
            var url2 = d.exotel.recordingUrl;
            console.log("url2", url2);
            y.CallRecordingPath = url2;
          } else {
            y.CallRecordingPath = "";
          }
        }

        if (typeof d["customFields"] != "undefined") {
          if (Array.isArray(d["customFields"])) {
            //CustomField Set For Array
            var customFields = d["customFields"];

            for (var each2 in customFields) {
              var c = customFields[each2];

              y["Custom : " + c.name] = c.value;
            }
          } else {
            //CustomField Set For Object
            for (var each in self.fieldList) {
              if (
                typeof d["customFields"] != "undefined" &&
                d["customFields"][self.fieldList[each].name]
              ) {
                var customVal = d["customFields"][self.fieldList[each].name];

                if (customVal) {
                  if (Array.isArray(customVal)) {
                    y[self.fieldList[each].name] = customVal.toString();
                  } else {
                    if (self.fieldList[each].type == "date") {
                      y[self.fieldList[each].name] = this.$moment(customVal)
                        .format()
                        .substr(0, 10);
                    } else {
                      y[self.fieldList[each].name] = customVal;
                    }
                  }
                } else {
                  y[self.fieldList[each].name] = "";
                }
              }
            }
          }
        }

        return y;
      });

      const newBook = XLSX.utils.book_new();
      const newSheet = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(newBook, newSheet, "Sheet1");
      XLSX.writeFile(newBook, `report_${new Date().getTime()}.xlsx`);
    },

    /** GET THE REQUIRED DATA */
    // getting all the logs depending on the selected user, list and daterange, limit and skip
    async fetchLogs(limit, skip) {
      try {
        this.loading = true;
        this.loadingText = "Loading... Please wait";

        this.snackbar = false;
        // Note: do NOT reset `this.statistics` here. It is owned by
        // `fetchGraphReports` and powers the KPI / contact-ratio computeds.
        // Clearing it on every page change made those cards flash to 0.
        var users = [];
        var lists = [];
        var dispos = [];

        //Get lists
        lists = this.getListIds();

        //Get users
        users = this.getUserIds();

        if (this.dates.length < 1) {
          this.dates = [
            moment()
              .subtract(7, "days")
              .format("YYYY-MM-DD"),
            moment().format("YYYY-MM-DD"),
          ];
        }

        if (limit == undefined || limit == undefined) {
          limit = 5;
          skip = 0;
        }

        // dispo variable
        if (_.includes(this.selectedDispos, "All Dispos")) {
          // dispos = _.map(this.dispos, "title");
          dispos = [];
        } else {
          dispos = this.selectedDispos;
        }

        //final dispos
        dispos = _.without(dispos, "All Dispos");

        // Check Search String null
        if (this.search == undefined || this.search == null) {
          this.search = "";
        }
        var res = await this.$http.post(this.ENDPOINT + "/logs/byFilter", {
          search: this.search,
          field: this.searchField,
          range: this.dates,
          lists: lists,
          users: users,
          dispos: dispos,
          limit: limit,
          skip: skip,
          count: false,
        });

        var response = [];
        response = _.without(
          _.map(res.body.data, (r) => {
            if (r.calledOn != undefined) {
              r.formatedTime = moment(r.calledOn).format("YYYY-MM-DD hh:mm A");
              r.time = moment(r.calledOn).format("hh:mm A");
              r.date = moment(r.calledOn).format("DD MMM YYYY");

              // Filter based on call direction
              if (this.selectedCallDirection !== "all") {
                if (r.type === "call") {
                  // For outgoing calls, either direction is 'outgoing' or undefined
                  if (this.selectedCallDirection === "outgoing") {
                    if (r.direction && r.direction !== "outgoing") {
                      return undefined;
                    }
                  }
                  // For incoming calls, direction must be explicitly 'incoming'
                  else if (this.selectedCallDirection === "incoming") {
                    if (r.direction !== "incoming") {
                      return undefined;
                    }
                  }
                }
              }

              if (typeof r.truecallerCallRecording != "undefined") {
                r.recording = r.truecallerCallRecording;
                r.recording.isPlaying = false;
                r.recording.playbackTime = 0;
              } else {
                r.recording = { success: false };
              }

              if (
                typeof r.telecmi != "undefined" &&
                (r.defaultDialer == "Telecmi" ||
                  r.defaultDialer == "Telecmi-Linphone")
              ) {
                if (typeof r.telecmi["filename"] != "undefined") {
                  r.recording = {
                    success: true,
                    filename: r.telecmi.filename,
                    url:
                      "https://s3.ap-south-1.amazonaws.com/telecmi/" +
                      r.telecmi.filename,
                  };

                  r.recording.isPlaying = false;
                  r.recording.playbackTime = 0;
                } else {
                  r.recording = { success: false };
                }
              }

              if (
                typeof r.exotel != "undefined" &&
                r.defaultDialer == "Phone Dialer with Rec"
              ) {
                if (typeof r.exotel["recordingUrl"] != "undefined") {
                  r.recording = {
                    success: true,
                    filename: r.exotel.recordingUrl,
                    url: r.exotel.recordingUrl,
                  };

                  r.recording.isPlaying = false;
                  r.recording.playbackTime = 0;
                } else {
                  r.recording = { success: false };
                }
              }

              // SIP Call Recording - handled by lazy loading in openSipRecording()
              // No need to process sipCallRecording here

              if (r.type == "call" && typeof r.defaultDialer == "undefined") {
                r.defaultDialer = "Phone Dialer";
              }

              if (r.type == "link") {
                r.defaultDialer = "";
              }

              if (typeof r.duration != "undefined" && r.type == "call") {
                var inSecs = r.duration / 1000;

                if (inSecs >= 60) {
                  r.Duration = `${Math.floor(inSecs / 60)}m ${Math.round(
                    inSecs % 60,
                  )}s`;
                } else {
                  r.Duration = `${Math.round(inSecs)}s`;
                }

                if (parseInt(r.duration) > 0) {
                  r.callStatus = "Connected";
                } else {
                  r.callStatus = "Not Connected";
                }
              }

              if (typeof r.timeSpentOnCall != "undefined" && r.type == "call") {
                var timeSpentOnCall = r.timeSpentOnCall / 1000;

                if (timeSpentOnCall > 60) {
                  r.TimeSpent = `${Number(timeSpentOnCall / 60).toFixed(
                    1,
                  )} mins`;
                } else {
                  r.TimeSpent = `${Number(timeSpentOnCall).toFixed(0)}s`;
                }
                if (
                  typeof r.duration != "undefined" ||
                  r.duration === "" ||
                  r.duration === "0s"
                ) {
                  r.duration = r.TimeSpent;
                }
              }

              if (r.type != "call") {
                r.dispo = "";
                r.note = "";
              }

              // accounts

              var account = _.find(this.allUsers, (user) => {
                return r.accountsId == user.id;
              });

              if (account) {
                r.accounts = account;
              }

              //lists
              var list = _.find(this.lists, (list) => {
                return r.listId == list.id;
              });
              if (list) {
                r.list = list;
              }

              //templates
              var template = _.find(this.templates, (template) => {
                return r.templateId == template.id;
              });
              if (template) {
                r.template = template;
              }
            }

            try {
              var output = _.mapValues(r.customFields, (value, key) => {
                var fieldDef = _.find(this.fieldList, (f) => {
                  return f.name == key;
                });

                if (!fieldDef) return value;

                switch (fieldDef.type) {
                  case "date":
                    if (this.$moment(value).isValid())
                      value = this.$moment(value)
                        .format()
                        .substr(0, 10);
                    else value = "";
                    break;
                  case "text":
                    value = value.toString().trim();
                    break;
                  case "number":
                    value = Number(value);
                    break;
                  case "mcq":
                    if (!_.includes(fieldDef.mcqOptions, value)) value = "";
                    break;
                  // case "checkbox":
                  //   if (!_.includes(fieldDef.checkboxOptions, value)) value = "";
                  //   break;
                }
                return value;
              });
              r.customFields = output;
            } catch (e) {
              console.log(e);
            }

            return r;
          }),
          undefined,
        );
        response = _.orderBy(response, "calledOn", "desc");

        this.totalConnectedCall = 0;
        this.totalNotConnectedCall = 0;

        var pieResponse = "";

        pieResponse = _.map(response, (d) => {
          if (d.type == "call") {
            if (parseInt(d.Duration) > 0) {
              this.totalConnectedCall++;
            } else {
              this.totalNotConnectedCall++;
            }
          }
        });

        //Pie Chart
        this.pieChartData = {
          labels: [
            `Connected Calls (${this.totalConnectedCall})`,
            `Not Connected Calls (${this.totalNotConnectedCall})`,
          ],
          datasets: [
            {
              backgroundColor: ["#2ecc71", "#FF6384"],
              data: [this.totalConnectedCall, this.totalNotConnectedCall],
            },
          ],
        };

        console.log("response 13124214325423", response);

        this.loading = false;
        this.loadingText = "";
        return response;
      } catch (e) {
        console.log(e);
        this.loading = false;
        this.loadingText = "Something went wrong while fetching call logs";
      }
    },
    /** Total logs count [ used for pagination ] */
    async fetchCount() {
      // this.loading = true;
      // this.loadingText = "Loading... Please wait";

      var users = [];
      var lists = [];
      var dispos = [];

      //Get lists
      lists = this.getListIds();

      //Get lists
      users = this.getUserIds();

      if (this.dates.length < 1) {
        this.dates = [
          moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
      }

      // dispo variable
      if (_.includes(this.selectedDispos, "All Dispos")) {
        // dispos = _.map(this.dispos, "title");
        dispos = [];
      } else {
        dispos = this.selectedDispos;
      }

      //final dispos
      dispos = _.without(dispos, "All Dispos");

      // Check Search string null
      if (this.search == undefined || this.search == null) {
        this.search = "";
      }

      this.$http
        .post(this.ENDPOINT + "/logs/byFilter", {
          search: this.search,
          field: this.searchField,
          range: this.dates,
          lists: lists,
          users: users,
          dispos: dispos,
          count: true,
        })
        .then((response) => {
          this.totalRecords = response.body;
          this.pageCount = Math.ceil(this.totalRecords / this.records);
        })
        .catch((e) => {});
    },
    async fetchTemplates() {
      var self = this;

      try {
        var res = await self.$http.get(
          self.$store.state.ENDPOINT +
            "/templates?filter[where][companyId]=" +
            self.$store.getters.user.companyId,
        );

        self.templates = res.body;
      } catch (e) {
        console.log(e);
      }
    },
    async fetchLists() {
      try {
        this.lists = [];
        var filter = {
          where: {
            companyId: this.$store.state.user.companyId,
          }
        };

        if (this.$store.getters.user.role.toLowerCase() != "manager") {
          filter.where["teamsId"] = {
            inq: _.without(_.map(this.teams, "id"), "allTeams"),
          };
        }

        for (let i of this.fieldList) {
          this.allHeaders.push({
            text: i.name,
            align: "left",
            sortable: false,
            value: "customFields." + i.name,
          });
        }

        var res = await this.$http.get(
          `${this.ENDPOINT}/lists/fetch-list-v2?filter=${encodeURIComponent(
            JSON.stringify(filter),
          )}`,
        );
        this.lists = res.body;

        if (
          typeof this.user.permissions.canAccessList != "undefined" &&
          this.user.permissions.canAccessList === false
        ) {
          this.lists = _.filter(this.lists, (list) => {
            return _.find(list.accounts, (account) => {
              return account.id == this.user.id;
            });
          });
        }

        this.lists.unshift({
          id: "allLists",
          name: "All Lists",
        });

        if (this.selectedList.length < 1) {
          this.selectedList.push(this.lists[0].id);
        }
      } catch (e) {
        console.log(e);
      }
    },

    getTeamIds() {
      var ids = [];

      if (this.selectedTeam == "allTeams") {
        ids = _.map(this.teams, "id");
      } else {
        ids.push(this.selectedTeam);
      }

      ids = _.without(ids, "allTeams");
      return ids;
    },

    // Fetch Accounts
    async fetchAccounts() {
      this.allUsers = [];

      try {
        var teams = [];
        teams = await this.getTeamIds();

        var response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/accounts/fetch-v2`,
          {
            teams: teams,
          },
        );
        this.allUsers = response.body;
        if (this.user.role == "Manager" || this.user.role == "Teammanager") {
          this.allUsers.unshift({
            id: "allMembers",
            name: "All Members",
          });
        }
        if (this.selectedUser.length < 1) {
          this.selectedUser.push(this.allUsers[0].id);
        }
      } catch (e) {
        if (e.body.error && e.body.error.message)
          return this.$swal({ type: "error", text: e.body.error.message });
        else
          return this.$swal({
            type: "error",
            text: "Error while fetching accounts",
          });
      }
    },

    async fetchDispos() {
      try {
        this.dispos = [];

        var res = await this.$http.get(
          this.ENDPOINT +
            "/dispos?filter[where][companyId]=" +
            this.user.companyId,
        );
        this.dispos = res.body;
        if (this.dispos.length > 0) {
          this.dispos.unshift({
            title: "All Dispos",
          });
        }

        if (this.selectedDispos.length < 1) {
          this.selectedDispos.push(this.dispos[0].title);
        }
      } catch (e) {
        console.log("dispo related Isususes", e);
      }
    },

    async fetchDeletedListIds() {
      try {
        const start = "2020-01-01";
        const end = this.$moment().format("YYYY-MM-DD");
        const response = await this.$http.post(
          `${this.ENDPOINT}/audits/fetch`,
          {
            users: [],
            action: "deleted",
            model: "list",
            start: start,
            end: end,
            limit: 1000,
            skip: 0,
          }
        );
        const auditData = response.body.data || [];
        this.deletedListIds = auditData
          .map((d) => d.target && d.target.id)
          .filter(Boolean);
      } catch (e) {
        console.error("Error fetching deleted list IDs:", e);
      }
    },

    /** GRAPH REPORT */
    fetchGraphReports() {
      this.preloader = true;
      this.statistics = [];

      var users = [];
      var lists = [];
      var dispos = [];

      //Get lists
      lists = this.getListIds();

      //Get lists
      users = this.getUserIds();

      if (this.dates.length < 1) {
        this.dates = [
          moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
      }

      // dispo variable
      if (_.includes(this.selectedDispos, "All Dispos")) {
        // dispos = _.map(this.dispos, "title");
        dispos = [];
      } else {
        dispos = this.selectedDispos;
      }

      //final dispos
      dispos = _.without(dispos, "All Dispos");

      this.$http
        .post(this.ENDPOINT + "/logs/v2/graph", {
          range: this.dates,
          lists: lists,
          users: users,
          dispos: dispos,
        })
        .then((response) => {
          response = response.body;

          response.forEach((stat) => {
            this.statistics.push({
              _id: stat._id,
              duration: stat.duration,
              count: stat.count,
            });
            for (var y in stat.status) {
              let exists = _.find(this.graphData, (s) => {
                return s._id == stat.status[y]["_id"];
              });
              let index = _.indexOf(this.graphData, exists);
              if (index > -1) {
                this.graphData[index].count =
                  this.graphData[index].count + stat.status[y]["count"];
                continue;
              }
              this.graphData.push({
                _id: stat.status[y]["_id"],
                count: stat.status[y]["count"],
              });
            }
          });
          this.fillBarData();
          this.fillData();

          this.preloader = false;

          // Kick off previous-period aggregate for KPI deltas (non-blocking).
          this.fetchPrevPeriodStats();
        })
        .catch((e) => {
          this.preloader = false;
        });
    },

    fillBarData() {
      this.loadChart = false;
      var counter = new Object();
      this.totalCallCount = 0;
      this.totalCallDuration = 0;

      this.statistics = _.map(this.statistics, (d) => {
        var obj = {};
        obj.account = _.find(this.allUsers, (a) => {
          return a.id == d._id.accountsId;
        });
        obj.accountsId = d._id.accountsId;
        obj.count = d.count;

        if (d.duration != undefined) {
          d.duration = d.duration / 1000;
          obj.duration = Number(d.duration / 60);
          this.totalCallDuration += obj.duration;
        }

        if (obj.account != undefined) {
          counter[obj.account.name] = d.count;
        }
        this.totalCallCount += obj.count;

        return obj;
      });
      this.totalCallDuration = `${this.totalCallDuration.toFixed(1)} mins`;

      //Bar Chart
      this.barChartData = {
        datasets: [
          {
            backgroundColor: "#4cd964",
            data: [..._.map(counter)],
          },
        ],
        // These labels appear in the legend and in the tooltips when hovering different arcs
        labels: Object.keys(counter),
      };

      this.loadChart = true;
    },
    async fillData() {
      const self = this;
      self.datacollection = {};
      self.loadChart = false;
      self.labelShown = false;

      var colors = [];
      this.graphData = _.orderBy(this.graphData, ["count"], ["desc"]);
      if (this.graphData.length > 10) {
        var graphNew = this.graphData.slice(0, 10);
        var graphOther = {
          _id: "Others",
          count: 0,
        };
        for (let i = 10; i < this.graphData.length; i++) {
          graphOther.count += this.graphData[i].count;
        }
        this.graphData = graphNew.concat(graphOther);
      }
      _.each(this.graphData, (val) => {
        val = _.find(self.dispoList, (d) => {
          return d.title == val._id;
        });

        if (val == undefined) {
          colors.push("#d9d9d9");
        } else {
          colors.push(val.hex);
        }
      });
      this.labelShown = false;

      var labels = _.map(
        this.graphData,
        (item) => `${item._id} (${item.count})`,
      );

      // //Chart
      self.datacollection = {
        datasets: [
          {
            backgroundColor: colors,
            data: _.map(this.graphData, "count"),
            borderWidth: 0,
          },
        ],
        // These labels appear in the legend and in the tooltips when hovering different arcs
        labels: labels,
      };

      self.labelShown = true;

      await self.$nextTick((err) => {
        self.loadChart = true;
        return;
      });
    },

    /** EXPORT (CSV) */
    async exportReport() {
      var self = this;
      this.loader1 = true;
      this.exportProgress = 0;

      try {
        var dispos = [];
        var lists = this.getListIds();
        var users = this.getUserIds();

        if (this.dates.length < 1) {
          this.dates = [
            moment().subtract(7, "days").format("YYYY-MM-DD"),
            moment().format("YYYY-MM-DD"),
          ];
        }

        if (_.includes(this.selectedDispos, "All Dispos")) {
          dispos = [];
        } else {
          dispos = this.selectedDispos;
        }
        dispos = _.without(dispos, "All Dispos");


        if (this.totalRecords < 5000) {
          var maxCount = 2500;
          var data = [], res;
          var totalBatches = Math.ceil(this.totalRecords / maxCount);
          this.totalBatches = totalBatches;
          var limit = 0, skip = 0;
          var processedRecords = 0;

          for (let currentBatch = 1; currentBatch <= totalBatches; currentBatch++) {
            this.currentBatch = currentBatch;
            skip += limit;
            limit = maxCount;
            res = await this.$http.post(`${this.ENDPOINT}/logs/byFilter`, {
              companyId: this.$store.getters.user.companyId,
              range: this.dates,
              lists: lists,
              users: users,
              dispos: dispos,
              limit: limit,
              skip: skip,
              count: false,
            });
            data = data.concat(res.body.data);
            processedRecords += res.body.data.length;
            this.exportProgress = Math.round((processedRecords / this.totalRecords) * 100);
          }

          this.exportProgress = 100;

          data = _.map(data, (r) => {
            var account = _.find(this.allUsers, (user) => r.accountsId == user.id);
            if (account) r.accounts = account;
            var list = _.find(this.lists, (list) => r.listId == list.id);
            if (list) r.list = list;
            var template = _.find(this.templates, (template) => r.templateId == template.id);
            if (template) r.template = template;
            return r;
          });

          var exportData = _.orderBy(data, "time", "desc");
          if (exportData.length < 1) {
            this.loader1 = false;
            return this.$swal({ type: "warning", text: "No records to export" });
          }

          exportData = _.map(exportData, (d) => {
            var y = { Phone: d.phone ? d.phone.toString() : "" };
            y.Name = d.name || "";
            y.Company = d.companyName || "";
            y.Date = d.calledOn ? this.$moment(d.calledOn).format("YYYY-MM-DD") : "";
            y.Time = d.calledOn ? this.$moment(d.calledOn).format("hh:mm a") : "";
            y.Leadscore = d.leadScore || "";
            y.Remarks = d.remarks || "";
            y.Extra = d.extra || "";
            y.CalledBy = d.accounts && d.accounts.name ? d.accounts.name : "";
            y.Direction = d.direction || "outgoing";
            y.List = d.list && d.list.name ? d.list.name : "";
            y.Disposition = d.dispo || "";
            y.CallbackOn = d.callbackOn ? this.$moment(d.callbackOn).format("DD-MM-YYYY, h:mm a") : "";
            y.Email = d.email || "";
            y.defaultDialer = d.type === "call" && d.defaultDialer === undefined ? "Phone Dialer" : d.defaultDialer || "";
            y.Team = d.list && d.list.teams ? d.list.teams.name : "";
            y.Notes = d.note || "";
            y["duration (sec)"] = d.duration ? Number(d.duration / 1000).toFixed(2) : "";
            y.CallStatus = d.type === "call" ? parseInt(d.duration) > 0 ? "Connected" : "Not Connected" : "";
            y["TimeSpent (sec)"] = d.timeSpentOnCall ? Number(d.timeSpentOnCall / 1000).toFixed(2) : "";
            var statusArray = [];
            if (d.emailSent) statusArray.push("Email");
            if (d.smsSent) statusArray.push("SMS");
            if (d.whatsappSent) statusArray.push("Whatsapp");
            if (d.linkSent) statusArray.push("Link");
            if (d.appSent) statusArray.push("App");
            if (d.called) statusArray.push("Call");
            y.Status = statusArray.length > 0 ? statusArray.join() : "";
            y.Template = d.template && d.template.title ? d.template.title : "";
            if (d.truecallerCallRecording && d.truecallerCallRecording.name && typeof d.truecallerCallRecording.name !== "undefined") {
              y.CallRecordingPath = `${this.$store.state.ENDPOINT}/storages/get/${d.truecallerCallRecording.name}?access_token=${this.$store.getters.user.token}`;
            } else {
              y.CallRecordingPath = "";
            }
            if (d.telecmi && (d.defaultDialer == "Telecmi" || d.defaultDialer == "Telecmi-Linphone")) {
              if (typeof d.telecmi.filename != "undefined") {
                y.CallRecordingPath = `https://rest.telecmi.com/v2/play?appid=${self.telecmiIntegration.config.appid}&secret=${self.telecmiIntegration.config.secret}&file=${d.telecmi.filename}`;
              } else {
                y.CallRecordingPath = "";
              }
            }
            if (d.exotel && d.defaultDialer == "Phone Dialer with Rec") {
              if (typeof d.exotel.recordingUrl != "undefined") {
                y.CallRecordingPath = d.exotel.godialRecordingUrl;
              } else {
                y.CallRecordingPath = "";
              }
            }
            if (typeof d["customFields"] != "undefined") {
              if (Array.isArray(d["customFields"])) {
                for (var each2 in d["customFields"]) {
                  var c = d["customFields"][each2];
                  y["Custom : " + c.name] = c.value;
                }
              } else {
                for (var each in self.fieldList) {
                  if (typeof d["customFields"] != "undefined" && d["customFields"][self.fieldList[each].name]) {
                    var customVal = d["customFields"][self.fieldList[each].name];
                    if (customVal) {
                      if (Array.isArray(customVal)) {
                        y[self.fieldList[each].name] = customVal.toString();
                      } else {
                        if (self.fieldList[each].type == "date") {
                          y[self.fieldList[each].name] = this.$moment(customVal).format().substr(0, 10);
                        } else {
                          y[self.fieldList[each].name] = customVal;
                        }
                      }
                    } else {
                      y[self.fieldList[each].name] = "";
                    }
                  }
                }
              }
            }
            return y;
          });

          const newBook = XLSX.utils.book_new();
          const newSheet = XLSX.utils.json_to_sheet(exportData);
          XLSX.utils.book_append_sheet(newBook, newSheet, "Sheet1");
          this.exportProgress = 130;
          XLSX.writeFile(newBook, `report_${new Date().getTime()}.xlsx`);
          this.loader1 = false;
          fb.log("report_export");

        } else {
          var response = await self.$http.post(
            `${self.ENDPOINT}/logs/exportv2`,
            {
              companyId: this.$store.getters.user.companyId,
              range: this.dates,
              lists: lists,
              users: users,
              dispos: dispos,
            }
          );

          const {
            batchId,
            fileName,
            totalBatches,
            totalRecords,
          } = response.body;

          let batchStatus = await this.fetchDetails(batchId);

          if (batchStatus.status == "complete") {
            var swalMessage = "Your file is ready to download";
            var swalType = "success";
            if (batchStatus.partialFailure) {
              swalMessage = `Your file is ready but ${batchStatus.failedBatches} batch(es) failed. Some records may be missing. Download anyway or retry?`;
              swalType = "warning";
            }
            await self
              .$swal({
                type: swalType,
                text: swalMessage,
                showCancelButton: true,
                confirmButtonColor: "#31a848",
                cancelButtonColor: "#F44336",
                confirmButtonText: batchStatus.partialFailure ? "Download Anyway" : "Download",
                cancelButtonText: batchStatus.partialFailure ? "Retry" : "Cancel",
              })
              .then(async (yes) => {
                if (yes.value) {
                  try {
                    var downloadResp = await self.$http.post(
                      `${self.ENDPOINT}/contacts/downloadExportBigData`,
                      { fileName: fileName },
                    );
                    if (downloadResp.body && downloadResp.body.url) {
                      self.downloadFileFromUrl(
                        downloadResp.body.url,
                        `report_${new Date().getTime()}.csv`,
                      );
                    } else {
                      self.$swal({
                        type: "error",
                        text: "Export file not found. Please try again.",
                      });
                    }
                  } catch (error) {
                    self.$swal({
                      type: "error",
                      text:
                        error.body && error.body.error
                          ? error.body.error.message
                          : "Something went wrong! Please try again.",
                    });
                  }
                } else {
                  if (batchStatus.partialFailure) {
                    self.loader1 = false;
                    self.exportProgress = 0;
                    self.exportReport();
                  }
                }
              });
          }
          self.loader1 = false;
          fb.log("report_export");
        }
      } catch (e) {
        console.log(e);
        this.loader1 = false;
        return this.$swal({
          type: "error",
          text: e.body && e.body.error ? e.body.error.message : "Connection error. Try again later.",
        });
      }
    },
    fetchDetails(batchId) {
      return new Promise((resolve, reject) => {
        const checkStatus = () => {
          this.$http
            .get(`${this.ENDPOINT}/exportBatchstatuses/${batchId}`)
            .then((response) => {
              if (response.body.status === "complete") {
                this.exportProgress = 100;
                if (response.body.failedBatches && response.body.failedBatches > 0) {
                  resolve({
                    ...response.body,
                    partialFailure: true,
                    failedBatches: response.body.failedBatches,
                  });
                } else {
                  resolve(response.body);
                }
              } else if (response.body.status === "failed") {
                this.$swal({
                  type: "error",
                  text: response.body.error,
                }).then(() => {
                  this.loader1 = false;
                  this.exportProgress = 0;
                  reject(new Error(response.body.error));
                });
              } else {
                const { completed, totalBatches } = response.body;
                this.currentBatch = completed || 0;
                this.totalBatches = totalBatches || 0;
                
                const percentage = Math.round((completed / totalBatches) * 100);
                if (response.body.status === "combining") {
                  this.exportProgress = 99;
                } else if (completed == totalBatches) {
                  this.exportProgress = 99;
                } else {
                  this.exportProgress = percentage;
                }
                setTimeout(checkStatus, 3000);
              }
            })
            .catch((error) => {
              console.error("Failed to fetch details:", error);
              reject(error);
            });
        };
        checkStatus();
      });
    },
    downloadFileFromUrl(fileUrl, downloadName) {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", downloadName || "report_export.csv");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    /* ============ ReportsV2 helper methods ============ */
    // Apply a named quick-range preset and reload.
    applyQuickRange(key) {
      const tz =
        (this.companyDetails && this.companyDetails.timezone) || "Asia/Kolkata";
      const now = moment.tz(tz);
      let start, end;
      switch (key) {
        case "today":
          start = now.clone().startOf("day");
          end = now.clone().endOf("day");
          break;
        case "yesterday":
          start = now
            .clone()
            .subtract(1, "day")
            .startOf("day");
          end = now
            .clone()
            .subtract(1, "day")
            .endOf("day");
          break;
        case "last7":
          start = now
            .clone()
            .subtract(6, "days")
            .startOf("day");
          end = now.clone().endOf("day");
          break;
        case "thisWeek":
          start = now.clone().startOf("isoWeek");
          end = now.clone().endOf("isoWeek");
          break;
        case "lastWeek":
          start = now
            .clone()
            .subtract(1, "week")
            .startOf("isoWeek");
          end = now
            .clone()
            .subtract(1, "week")
            .endOf("isoWeek");
          break;
        case "thisMonth":
          start = now.clone().startOf("month");
          end = now.clone().endOf("month");
          break;
        default:
          return;
      }
      this.quickRange = key;
      this.dates = [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")];
      this.load();
    },
    onCustomRangeSelected() {
      // Ensure this.dates is sorted and always has exactly 2 elements
      let d1 = this.dates[0];
      let d2 = this.dates[1] || d1;
      let start = this.$moment(d1);
      let end = this.$moment(d2);
      if (start.valueOf() > end.valueOf()) {
        let temp = start;
        start = end;
        end = temp;
      }
      
      // Mutate the array to maintain reactivity reference
      this.dates.splice(0, this.dates.length, start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
      
      // Called from the custom-range date picker's Save handler.
      if (this.$refs.dialog && this.$refs.dialog.save) {
        this.$refs.dialog.save(this.dates);
      }
      
      this.quickRange = "custom";
      this.load();
    },
    setSearchField(value) {
      this.searchField = value;
      if (this.search) this.query();
    },
    onRecordsChange() {
      // When rows-per-page changes, restart from page 1 so we don't end up
      // on a page index beyond the new pageCount.
      this.page = 1;
      this.pagination();
    },
    resetColumnsToDefault() {
      // Reset visible columns to the default set declared in data().
      const defaults = _.filter(this.allHeaders, (h) =>
        this.defaultHeaderValues.includes(h.value),
      );
      this.$store.commit("setReportHeaders", defaults);
    },
    toTitle(s) {
      if (!s) return "";
      return String(s)
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatMs(secs) {
      // "Xm Ys" / "Ys"
      const n = Math.max(0, Math.round(secs || 0));
      if (n < 60) return `${n}s`;
      return `${Math.floor(n / 60)}m ${n % 60}s`;
    },
    formatHm(secs) {
      // "Xh Ym" / "Ym"
      const n = Math.max(0, Math.round(secs || 0));
      const h = Math.floor(n / 3600);
      const m = Math.floor((n % 3600) / 60);
      if (h === 0) return `${m}m`;
      return `${h}h ${m}m`;
    },
    deltaInfo(cur, prev, suffix, isPercentagePoint = false) {
      // Returns { sub, deltaClass } describing change vs previous period.
      if (!prev || prev === 0) {
        if (!cur) return { sub: "No data for last period", deltaClass: "flat" };
        return { sub: `New (${suffix})`, deltaClass: "up" };
      }
      const diff = cur - prev;
      if (Math.abs(diff) < 0.0001)
        return { sub: `No change ${suffix}`, deltaClass: "flat" };
      const dir = diff > 0 ? "up" : "down";
      const sign = diff > 0 ? "+" : "−";
      if (isPercentagePoint) {
        return {
          sub: `${sign}${Math.abs(Math.round(diff))} pts ${suffix}`,
          deltaClass: dir,
        };
      }
      const pct = Math.round((Math.abs(diff) / prev) * 100);
      return { sub: `${sign}${pct}% ${suffix}`, deltaClass: dir };
    },
    dispoBadge(title) {
      // Prefer the disposition's own color (configured per-tenant on the
      // disposition object as `hex`). Fall back to a category-based palette
      // when the disposition isn't in the store.
      const t = String(title || "");
      const match = _.find(
        this.dispoList || [],
        (d) => String(d.title || "").toLowerCase() === t.toLowerCase(),
      );
      if (match && match.hex) {
        const hex = String(match.hex).trim();
        return { bg: this._tintHex(hex, 0.18), fg: this._readableFg(hex) };
      }
      const k = t.toUpperCase();
      const green = [
        "INTERESTED",
        "DEMO SCHEDULED",
        "DEMO-SCHEDULED",
        "SUPPORT",
      ];
      const amber = ["WILL CALL LATER", "HAVE QUERIES", "CALLBACK"];
      const blue = ["UNDER PROCESS", "CRC SUPPORT"];
      const red = [
        "NOT INTERESTED",
        "FOREIGN",
        "BUSY",
        "UNREACHABLE",
        "NOT PICKED UP",
        "NOT-PICKED-UP",
      ];
      if (green.includes(k)) return { bg: "#e9f9ef", fg: "#1b8a4c" };
      if (amber.includes(k)) return { bg: "#fff5e0", fg: "#a76b00" };
      if (blue.includes(k)) return { bg: "#e6f0ff", fg: "#1b4ea6" };
      if (red.includes(k)) return { bg: "#fde8ea", fg: "#a8323e" };
      return { bg: "#eceff1", fg: "#455a64" };
    },
    /** Mix a hex color toward white by `alpha` (0..1) to get a soft tint. */
    _tintHex(hex, alpha) {
      const h = hex.replace("#", "");
      const full =
        h.length === 3
          ? h
              .split("")
              .map((c) => c + c)
              .join("")
          : h.padEnd(6, "0");
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      const mix = (c) => Math.round(c * alpha + 255 * (1 - alpha));
      const toHex = (n) => n.toString(16).padStart(2, "0");
      return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
    },
    /**
     * Darken a hex color until it has enough contrast to be readable on a
     * light tinted background. Keeps the original hue. Falls back to a
     * neutral dark grey for near-white inputs.
     */
    _readableFg(hex) {
      const h = hex.replace("#", "");
      const full =
        h.length === 3
          ? h
              .split("")
              .map((c) => c + c)
              .join("")
          : h.padEnd(6, "0");
      let r = parseInt(full.slice(0, 2), 16);
      let g = parseInt(full.slice(2, 4), 16);
      let b = parseInt(full.slice(4, 6), 16);
      // Relative luminance per WCAG.
      const lum = (rr, gg, bb) => {
        const f = (c) => {
          c = c / 255;
          return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        return 0.2126 * f(rr) + 0.7152 * f(gg) + 0.0722 * f(bb);
      };
      // Darken iteratively until luminance is below threshold (~ AA on a
      // very light tint).
      let safety = 12;
      while (lum(r, g, b) > 0.32 && safety-- > 0) {
        r = Math.floor(r * 0.7);
        g = Math.floor(g * 0.7);
        b = Math.floor(b * 0.7);
      }
      const toHex = (n) =>
        Math.max(0, Math.min(255, n))
          .toString(16)
          .padStart(2, "0");
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    },
    formatDispoLabel(item) {
      const k = String((item && item.dispo) || "").toUpperCase();
      if (k === "CALLBACK" && item.calledOn) {
        return `Callback (${moment(item.calledOn).format("MMM D")})`;
      }
      return this.toTitle(item.dispo);
    },
    durationBadgeColor(item) {
      const seconds = this.parseDurationSeconds(
        (item && item.Duration) || (item && item.duration),
      );
      if (seconds === 0) return "#FF6384";
      if (seconds > 0) return "#31a848";
      return "#90a4ae";
    },
    parseDurationSeconds(value) {
      if (value === null || typeof value === "undefined" || value === "") return null;
      if (typeof value === "number") return value > 1000 ? value / 1000 : value;

      const text = String(value).trim().toLowerCase();
      if (!text) return null;

      const minuteMatch = text.match(/(\d+(?:\.\d+)?)\s*m(?:in(?:ute)?s?)?/);
      const secondMatch = text.match(/(\d+(?:\.\d+)?)\s*s(?:ec(?:ond)?s?)?/);

      if (minuteMatch || secondMatch) {
        const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
        const seconds = secondMatch ? Number(secondMatch[1]) : 0;
        return minutes * 60 + seconds;
      }

      const seconds = Number(text);
      return Number.isFinite(seconds) ? seconds : null;
    },
    formatDurationCell(item) {
      // Prefer the pre-formatted r.Duration string set in fetchLogs.
      if (item && typeof item.Duration === "string" && item.Duration.length) {
        return item.Duration;
      }
      const secs = Number((item && item.duration) || 0) / 1000;
      return this.formatMs(secs);
    },
    isHeaderVisible(value) {
      return _.some(this.reportHeaders, (h) => h.value === value);
    },
    /**
     * Click-and-drag horizontal scrolling for the daily trend strip.
     */
    trendDragStart(e) {
      const el = e.currentTarget;
      this._trendDrag = {
        el,
        active: true,
        startX: e.pageX - el.offsetLeft,
        scrollLeft: el.scrollLeft,
        moved: false,
      };
      el.classList.add("rv2-trend--dragging");
    },
    trendDragEnd() {
      if (!this._trendDrag) return;
      this._trendDrag.el.classList.remove("rv2-trend--dragging");
      this._trendDrag.active = false;
    },
    trendDragMove(e) {
      const d = this._trendDrag;
      if (!d || !d.active) return;
      e.preventDefault();
      const x = e.pageX - d.el.offsetLeft;
      const walk = x - d.startX;
      if (Math.abs(walk) > 2) d.moved = true;
      d.el.scrollLeft = d.scrollLeft - walk;
    },
    /**
     * Make data-table columns user-resizable.
     * Appends a thin drag-grip to each <th> in the V2 data table, drags
     * change the th's inline width. Widths are kept per-column in
     * `_columnWidths` (session only) so re-renders restore them.
     * Idempotent — safe to call repeatedly.
     */
    attachColumnResizers() {
      const tableEl =
        this.$refs.reportsDataTable && this.$refs.reportsDataTable.$el;
      if (!tableEl) return;
      const ths = tableEl.querySelectorAll(".v-data-table__wrapper thead th");
      if (!ths || ths.length === 0) return;

      if (!this._columnWidths) this._columnWidths = {};

      ths.forEach((th, idx) => {
        // Restore any persisted width for this column index.
        const saved = this._columnWidths[idx];
        if (saved) {
          th.style.width = saved + "px";
          th.style.minWidth = saved + "px";
        }
        if (th.querySelector(".rv2-col-resizer")) return; // already wired

        // Make th a positioning context for the absolute-positioned grip.
        if (getComputedStyle(th).position === "static") {
          th.style.position = "relative";
        }
        const grip = document.createElement("div");
        grip.className = "rv2-col-resizer";
        th.appendChild(grip);

        let startX = 0;
        let startWidth = 0;
        const onMove = (e) => {
          const delta = e.clientX - startX;
          const next = Math.max(48, startWidth + delta);
          th.style.width = next + "px";
          th.style.minWidth = next + "px";
        };
        const onUp = () => {
          document.removeEventListener("mousemove", onMove);
          document.removeEventListener("mouseup", onUp);
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
          // Persist final width in our session map.
          this._columnWidths[idx] = th.getBoundingClientRect().width;
        };
        grip.addEventListener("mousedown", (e) => {
          e.preventDefault();
          e.stopPropagation();
          // First drag: snapshot every column's natural width so the
          // remaining columns keep their current sizing once we flip
          // the table to fixed layout.
          if (!tableEl.classList.contains("rv2-table--resized")) {
            ths.forEach((otherTh, otherIdx) => {
              const w = otherTh.getBoundingClientRect().width;
              otherTh.style.width = w + "px";
              otherTh.style.minWidth = w + "px";
              this._columnWidths[otherIdx] = w;
            });
            tableEl.classList.add("rv2-table--resized");
          }
          startX = e.clientX;
          startWidth = th.getBoundingClientRect().width;
          document.body.style.cursor = "col-resize";
          document.body.style.userSelect = "none";
          document.addEventListener("mousemove", onMove);
          document.addEventListener("mouseup", onUp);
        });
      });
    },
    async fetchPrevPeriodStats() {
      // Mirror the current date range over to the previous span and fetch
      // aggregated stats for KPI deltas. Failure is non-fatal.
      try {
        if (!this.dates || this.dates.length === 0) return;
        const a = moment(this.dates[0]);
        const b = moment(this.dates[1] || this.dates[0]);
        const spanDays = Math.max(0, b.diff(a, "days"));
        const prevEnd = a.clone().subtract(1, "day");
        const prevStart = prevEnd.clone().subtract(spanDays, "days");

        // Build filter inputs the same way fetchGraphReports does.
        const lists = this.getListIds();
        const users = this.getUserIds();
        let dispos = _.includes(this.selectedDispos, "All Dispos")
          ? []
          : this.selectedDispos;
        dispos = _.without(dispos, "All Dispos");

        const resp = await this.$http.post(`${this.ENDPOINT}/logs/v2/graph`, {
          range: [prevStart.format("YYYY-MM-DD"), prevEnd.format("YYYY-MM-DD")],
          lists,
          users,
          dispos,
        });

        // Response is an array of per-user stats with `count`, `duration`
        // and a nested `status[]` of dispositions.
        const arr = (resp && resp.body) || [];
        let totalCalls = 0;
        let totalTalkSecs = 0;
        const dispoCounts = {};
        const unconnected = [
          "NOT PICKED UP",
          "NOT-PICKED-UP",
          "FOREIGN",
          "BUSY",
          "UNREACHABLE",
        ];
        let connectedCalls = 0;

        _.each(arr, (s) => {
          totalCalls += s.count || 0;
          const raw = typeof s.duration === "number" ? s.duration : 0;
          // Same heuristic as currentPeriodTotals: large values are ms, small
          // values are minutes (post-fillBarData reshape).
          if (raw > 10000) totalTalkSecs += raw / 1000;
          else totalTalkSecs += raw * 60;
          _.each(s.status || [], (st) => {
            const id = String(st._id || "").toUpperCase();
            dispoCounts[id] = (dispoCounts[id] || 0) + (st.count || 0);
            if (!unconnected.includes(id)) connectedCalls += st.count || 0;
          });
        });

        const topKey = _.maxBy(_.keys(dispoCounts), (k) => dispoCounts[k]);
        this.prevPeriodTotals = {
          totalCalls,
          totalTalkSecs,
          connectedCalls,
          topDispo: topKey ? { _id: topKey, count: dispoCounts[topKey] } : null,
        };
      } catch (e) {
        // Silent — KPI deltas are progressive enhancement.
        this.prevPeriodTotals = {
          totalCalls: 0,
          totalTalkSecs: 0,
          connectedCalls: 0,
          topDispo: null,
        };
      }
    },
  },
};
</script>

<style scoped>
/* =================================================================
   ReportsV2 styles (rv2-*) — new master report design
   Coexists with the older Reports.vue styles below (still scoped).
   ================================================================= */
.rv2-wrap {
  max-width: 1400px;
  margin: 0 auto;
}
.rv2-header-export {
  text-transform: none !important;
  letter-spacing: 0;
  font-weight: 600 !important;
  color: #1b8a4c !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}
.rv2-header-export:hover {
  background: #f5f5f5 !important;
}

.rv2-title {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}
.rv2-sub {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}
.theme--dark .rv2-sub {
  color: rgba(255, 255, 255, 0.7);
}

.rv2-card {
  border-radius: 10px !important;
  background: #fff;
}

.rv2-filter-card {
  overflow: hidden;
  position: relative;
}

.rv2-filter-card::before {
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  content: "";
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.rv2-accent-card {
  overflow: hidden;
  position: relative;
}

.rv2-accent-card::before {
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  content: "";
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.theme--dark .rv2-card {
  background: #1e1e1e;
}

.rv2-section-title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
}
.theme--dark .rv2-section-title {
  color: rgba(255, 255, 255, 0.7);
}

.rv2-quick-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
.theme--dark .rv2-quick-label {
  color: rgba(255, 255, 255, 0.7);
}
.rv2-preset {
  text-transform: none !important;
  letter-spacing: 0;
  font-weight: 500;
}
.rv2-date-pill {
  background: rgba(0, 0, 0, 0.03) !important;
  font-weight: 500;
}

/* KPI strip */
.rv2-kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}
.rv2-kpi-cell {
  flex: 1 1 0;
  min-width: 160px;
}
.rv2-kpi {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 14px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.theme--dark .rv2-kpi {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.12);
}
.rv2-kpi-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 6px;
}
.theme--dark .rv2-kpi-label {
  color: rgba(255, 255, 255, 0.6);
}
.rv2-kpi-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 4px;
}
.rv2-kpi-sub {
  font-size: 11px;
  font-weight: 500;
}
.rv2-kpi-sub.up {
  color: #1b8a4c;
}
.rv2-kpi-sub.down {
  color: #c0392b;
}
.rv2-kpi-sub.flat,
.rv2-kpi-sub.neutral {
  color: rgba(0, 0, 0, 0.55);
}
.theme--dark .rv2-kpi-sub.flat,
.theme--dark .rv2-kpi-sub.neutral {
  color: rgba(255, 255, 255, 0.6);
}

/* Chart cards */
.rv2-chart-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.rv2-chart-title {
  font-size: 14px;
  font-weight: 600;
}
.rv2-chart-sub {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.theme--dark .rv2-chart-sub {
  color: rgba(255, 255, 255, 0.6);
}
.rv2-chart-foot {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
}
.theme--dark .rv2-chart-foot {
  color: rgba(255, 255, 255, 0.55);
}
.rv2-empty {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
  padding: 24px 8px;
}
.theme--dark .rv2-empty {
  color: rgba(255, 255, 255, 0.5);
}

/* Disposition horizontal bars */
.rv2-bars {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rv2-bar-row {
  display: grid;
  grid-template-columns: 130px 1fr 44px;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}
.rv2-bar-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rv2-bar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  height: 10px;
  overflow: hidden;
}
.theme--dark .rv2-bar-track {
  background: rgba(255, 255, 255, 0.08);
}
.rv2-bar-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.25s ease;
}
.rv2-bar-count {
  text-align: right;
  font-weight: 600;
}

/* Contact ratio ring */
.rv2-ring-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.rv2-ring {
  width: 130px;
  height: 130px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rv2-ring::after {
  content: "";
  position: absolute;
  inset: 14px;
  background: #fff;
  border-radius: 50%;
}
.theme--dark .rv2-ring::after {
  background: #1e1e1e;
}
.rv2-ring-center {
  position: relative;
  z-index: 1;
  font-size: 22px;
  font-weight: 700;
}
.rv2-ring-legend {
  font-size: 12px;
  line-height: 1.8;
  width: 100%;
}
.rv2-ring-legend > div {
  display: grid;
  grid-template-columns: 14px 1fr auto;
  align-items: center;
  gap: 6px;
}
.rv2-ring-legend strong {
  text-align: right;
  font-weight: 600;
}
.rv2-legend-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rv2-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}
.rv2-dot.connected {
  background: #2ecc71;
}
.rv2-dot.notconnected {
  background: #e0e0e0;
}

.rv2-low-data {
  text-align: center;
  padding: 8px 0;
}
.rv2-low-data-num {
  font-size: 28px;
  font-weight: 700;
}
.rv2-low-data-text {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.theme--dark .rv2-low-data-text {
  color: rgba(255, 255, 255, 0.6);
}
.rv2-low-data-mini {
  font-size: 11px;
  text-align: left;
}

/* Daily trend mini bar chart */
.rv2-trend {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 122px;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 6px;
  cursor: grab;
  user-select: none;
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(55, 207, 55, 0.4) transparent;
}
.rv2-trend--dragging {
  cursor: grabbing;
  scroll-behavior: auto;
}
.rv2-trend--dragging .rv2-trend-bar {
  transition: none;
}
.rv2-trend::-webkit-scrollbar {
  height: 4px;
}
.rv2-trend::-webkit-scrollbar-track {
  background: transparent;
}
.rv2-trend::-webkit-scrollbar-thumb {
  background: rgba(55, 207, 55, 0.3);
  border-radius: 2px;
  transition: background 0.2s ease;
}
.rv2-trend:hover::-webkit-scrollbar-thumb {
  background: rgba(55, 207, 55, 0.55);
}
.theme--dark .rv2-trend {
  scrollbar-color: rgba(102, 187, 106, 0.45) transparent;
}
.theme--dark .rv2-trend::-webkit-scrollbar-thumb {
  background: rgba(102, 187, 106, 0.35);
}
.theme--dark .rv2-trend:hover::-webkit-scrollbar-thumb {
  background: rgba(102, 187, 106, 0.6);
}
.rv2-trend-col {
  flex: 0 0 22px;
  min-width: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
}
.rv2-trend-count {
  color: #151515;
  font-size: 10px;
  font-weight: 700;
  height: 14px;
  line-height: 14px;
  margin-bottom: 3px;
  text-align: center;
}
.theme--dark .rv2-trend-count {
  color: #f5f5f5;
}
.rv2-trend-bar-wrap {
  align-items: flex-end;
  display: flex;
  height: 78px;
  justify-content: center;
  width: 100%;
}
.rv2-trend-bar {
  width: 8px;
  min-height: 2px;
  background: #37cf37;
  border-radius: 3px 3px 0 0;
  transition: height 0.25s ease;
}
.rv2-trend-label {
  font-size: 10px;
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1;
}
.theme--dark .rv2-trend-label {
  color: rgba(255, 255, 255, 0.6);
}

/* Table toolbar */
.rv2-scope-chip {
  text-transform: none !important;
  letter-spacing: 0;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.04);
}
.theme--dark .rv2-scope-chip {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.08);
}

.rv2-col-panel {
  min-width: 280px;
}
.rv2-col-group {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 4px;
}
.theme--dark .rv2-col-group {
  color: rgba(255, 255, 255, 0.65);
}
.rv2-col-check ::v-deep .v-label {
  font-size: 13px;
}
.rv2-reset {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
}

/* Dispo chip and play */
.rv2-dispo-chip {
  font-weight: 500 !important;
  letter-spacing: 0;
}
.rv2-play-btn {
  background: rgba(0, 0, 0, 0.04);
}
.theme--dark .rv2-play-btn {
  background: rgba(255, 255, 255, 0.08);
}

/* Footer */
.rv2-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 10px;
}
.theme--dark .rv2-footer {
  border-color: rgba(255, 255, 255, 0.1);
}
.rv2-records-count {
  font-size: 12px;
}

.rv2-head-btn {
  text-transform: none !important;
  letter-spacing: 0;
  font-weight: 500;
}

/* Classic / Modern view toggle (action bar) */
.rv2-action-bar {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
}
.rv2-classic-view-toggle {
  grid-column: 2;
  justify-self: center;
}
.rv2-action-bar .rv2-head-btn {
  grid-column: 3;
  justify-self: end;
}
.classic-view-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #333833;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 10px;
  height: 36px;
  justify-content: center;
  padding: 0 4px;
  white-space: nowrap;
}
.classic-view-toggle:focus-visible {
  border-radius: 999px;
  box-shadow: 0 0 0 3px rgba(25, 155, 56, 0.2);
  outline: none;
}
.classic-view-toggle:hover .classic-view-toggle-label {
  color: #199b38;
}
.classic-view-toggle-label {
  color: #6f746e;
  transition: color 0.18s ease;
}
.classic-view-classic-label {
  color: #199b38;
}
.classic-view-switch {
  background: #cfd4cd;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: inline-flex;
  height: 20px;
  padding: 2px;
  transition: background 0.18s ease;
  width: 40px;
}
.classic-view-switch-thumb {
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
  height: 16px;
  transition: transform 0.18s ease;
  width: 16px;
}
.classic-view-toggle-active .classic-view-classic-label {
  color: #6f746e;
}
.classic-view-toggle-active .classic-view-modern-label {
  color: #199b38;
}
.classic-view-toggle-active .classic-view-switch {
  background: #199b38;
}
.classic-view-toggle-active .classic-view-switch-thumb {
  transform: translateX(18px);
}

/* Compact table cells */
.rv2-table ::v-deep th {
  font-size: 11px !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55) !important;
}
.rv2-table ::v-deep td {
  font-size: 13px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Only when the user has resized a column do we switch to fixed layout
   so the dragged widths are respected and overflow can ellipsis. */
.rv2-table.rv2-table--resized ::v-deep .v-data-table__wrapper > table {
  table-layout: fixed;
}
.rv2-table.rv2-table--resized ::v-deep td {
  max-width: 0;
}
/* Column resize grip — sits on the right edge of each <th>. */
.rv2-table ::v-deep thead th {
  position: relative;
}
.rv2-table ::v-deep .rv2-col-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  z-index: 2;
}
.rv2-table ::v-deep .rv2-col-resizer:hover {
  background: rgba(25, 118, 210, 0.25);
}
/* Compact-but-comfortable search field. Uses Vuetify dense default (~38px)
   and tightens font/icon sizes. */
.rv2-search ::v-deep .v-input__control,
.rv2-search ::v-deep .v-input__slot {
  min-height: 38px !important;
}
.rv2-search ::v-deep .v-input__slot {
  padding: 0 12px !important;
}
.rv2-search ::v-deep .v-text-field__slot input {
  font-size: 13px;
}
.rv2-search ::v-deep .v-input__prepend-inner,
.rv2-search ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center;
  height: 100%;
  display: inline-flex;
  align-items: center;
}
.rv2-search ::v-deep .v-input__append-inner .rv2-scope-chip {
  height: 22px;
  margin-right: 4px;
}
.rv2-search ::v-deep .v-input__prepend-inner .v-icon,
.rv2-search ::v-deep .v-input__append-inner .v-icon {
  font-size: 18px;
}
/* Filter selects / autocompletes — same height as search, tidy chips inside. */
.rv2-filter ::v-deep .v-input__control,
.rv2-filter ::v-deep .v-input__slot {
  min-height: 38px !important;
}
.rv2-filter ::v-deep .v-input__slot {
  padding: 0 12px !important;
}
.rv2-filter ::v-deep .v-select__slot,
.rv2-filter ::v-deep .v-text-field__slot {
  min-height: 38px;
}
.rv2-filter ::v-deep input,
.rv2-filter ::v-deep .v-select__selection {
  font-size: 13px;
}
.rv2-filter ::v-deep .v-select__selections {
  min-height: 38px !important;
  padding: 0 !important;
  flex-wrap: nowrap;
  overflow: hidden;
  align-items: center;
}
.rv2-filter ::v-deep .v-label {
  font-size: 13px;
}
.rv2-filter ::v-deep .v-input__append-inner {
  margin-top: 8px !important;
  padding-left: 0 !important;
}
.rv2-filter ::v-deep .v-input__append-inner .v-icon {
  font-size: 18px;
}
/* Inline chip(s) inside selects — centered, no awkward vertical drift. */
.rv2-filter ::v-deep .v-chip.v-size--x-small {
  height: 22px;
  margin: 0 4px 0 0 !important;
}
.rv2-filter ::v-deep .v-chip.v-size--x-small .v-chip__content {
  padding: 0 8px;
}
/* Hover/focus polish */
.rv2-filter ::v-deep .v-input--is-focused fieldset,
.rv2-search ::v-deep .v-input--is-focused fieldset {
  border-width: 1px;
}
/* Phone cell — leading round badge that color-codes the contact type. */
.rv2-phone-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.rv2-phone-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex: 0 0 22px;
}
.rv2-phone-badge .v-icon {
  color: inherit !important;
}
.rv2-phone-badge--in {
  background: rgba(25, 118, 210, 0.12);
  color: #1976d2;
}
.rv2-phone-badge--out {
  background: rgba(55, 207, 55, 0.15);
  color: #2ba82b;
}
.rv2-phone-badge--sms {
  background: rgba(123, 31, 162, 0.12);
  color: #7b1fa2;
}
.rv2-phone-badge--email {
  background: rgba(2, 136, 209, 0.12);
  color: #0288d1;
}
.rv2-phone-badge--link {
  background: rgba(245, 124, 0, 0.12);
  color: #f57c00;
}
.rv2-phone-badge--app {
  background: rgba(97, 97, 97, 0.12);
  color: #616161;
}
.rv2-phone-number {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---------- Dark theme polish ---------- */
.theme--dark .rv2-table ::v-deep th {
  color: rgba(255, 255, 255, 0.65) !important;
  background: transparent !important;
}
.theme--dark .rv2-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}
.theme--dark .rv2-table ::v-deep .rv2-col-resizer:hover {
  background: rgba(144, 202, 249, 0.3);
}
.theme--dark .rv2-low-data-mini {
  color: rgba(255, 255, 255, 0.7);
}
.theme--dark .rv2-records-count {
  color: rgba(255, 255, 255, 0.7);
}
/* Phone badges in dark — slightly brighter tints */
.theme--dark .rv2-phone-badge--in {
  background: rgba(100, 181, 246, 0.18);
  color: #90caf9;
}
.theme--dark .rv2-phone-badge--out {
  background: rgba(55, 207, 55, 0.22);
  color: #81c784;
}
.theme--dark .rv2-phone-badge--sms {
  background: rgba(186, 104, 200, 0.2);
  color: #ce93d8;
}
.theme--dark .rv2-phone-badge--email {
  background: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
}
.theme--dark .rv2-phone-badge--link {
  background: rgba(255, 167, 38, 0.2);
  color: #ffb74d;
}
.theme--dark .rv2-phone-badge--app {
  background: rgba(189, 189, 189, 0.18);
  color: #bdbdbd;
}
/* Date pill in dark */
.theme--dark .rv2-date-pill {
  background: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.85);
}
/* KPI sub variations stay theme-aware */
.theme--dark .rv2-kpi-sub.up {
  color: #66bb6a;
}
.theme--dark .rv2-kpi-sub.down {
  color: #ef5350;
}

/* Responsive tweaks */
@media (max-width: 600px) {
  .rv2-title {
    font-size: 18px;
  }
  .rv2-kpi-value {
    font-size: 18px;
  }
  .rv2-kpi-cell {
    flex: 1 1 calc(50% - 12px);
    min-width: 0;
  }
  .rv2-ring {
    width: 100px;
    height: 100px;
  }
  .rv2-ring-center {
    font-size: 18px;
  }
  .rv2-bar-row {
    grid-template-columns: 100px 1fr 36px;
  }
}

/* =================================================================
   Legacy Reports styles (preserved from Reports.vue)
   ================================================================= */
.reports-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .reports-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  padding: 32px;
  color: white;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.3);
}

.header-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.feature-pills {
  margin-top: 24px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 6px 14px;
  border-radius: 999px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  height: 100%;
  min-height: 36px;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

/* Tablet view fix for feature pills */
@media (max-width: 960px) {
  .feature-pill {
    padding: 10px 12px;
    font-size: 12px;
  }

  .feature-pills {
    margin-top: 16px;
  }
}

.modern-card {
  background: var(--v-background-base, white);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.customBorderRadius {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.card-header-green {
  height: 4px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
}

.chart-card {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
}

.theme--dark .chart-card {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

/* Fix filter dropdowns vertical centering */
.reports-container ::v-deep .v-autocomplete .v-input__control,
.reports-container ::v-deep .v-select .v-input__control,
.reports-container ::v-deep .v-text-field .v-input__control {
  min-height: 48px !important;
}

.reports-container ::v-deep .v-autocomplete .v-input__slot,
.reports-container ::v-deep .v-select .v-input__slot,
.reports-container ::v-deep .v-text-field .v-input__slot {
  min-height: 48px !important;
  align-items: center !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.reports-container ::v-deep .v-autocomplete .v-select__selections,
.reports-container ::v-deep .v-select .v-select__selections {
  min-height: 48px !important;
  padding: 8px 0 !important;
  align-items: center !important;
  flex-wrap: nowrap !important;
}

.reports-container ::v-deep .v-autocomplete .v-select__selections .v-chip,
.reports-container ::v-deep .v-select .v-select__selections .v-chip {
  margin: 0 4px 0 0 !important;
  max-width: 100px !important;
}

.reports-container
  ::v-deep
  .v-autocomplete
  .v-select__selections
  .v-chip
  .v-chip__content,
.reports-container
  ::v-deep
  .v-select
  .v-select__selections
  .v-chip
  .v-chip__content {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  display: block !important;
  max-width: 100% !important;
}

.reports-container ::v-deep .v-autocomplete .v-select__selections .v-chip span,
.reports-container ::v-deep .v-select .v-select__selections .v-chip span {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  display: block !important;
  margin-top: -1px !important;
}

/* Center the label when not focused/dirty */
.reports-container ::v-deep .v-autocomplete .v-label,
.reports-container ::v-deep .v-select .v-label,
.reports-container ::v-deep .v-text-field .v-label {
  top: 50% !important;
  transform: translateY(-50%) !important;
}

/* Move label to top when focused or has value */
.reports-container ::v-deep .v-input--is-label-active .v-label,
.reports-container ::v-deep .v-input--is-focused .v-label,
.reports-container ::v-deep .v-input--is-dirty .v-label {
  top: 0 !important;
  transform: translateY(-18px) scale(0.75) !important;
}

@media (max-width: 960px) {
  .header-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .page-title {
    font-size: 28px;
  }
}

/* Table Scroll Controls */
.scroll-controls {
  margin-right: 8px;
}

.scroll-btn {
  border: 1px solid #e0e0e0;
  border-radius: 8px !important;
  background: #fff !important;
  transition: all 0.2s ease;
}

.scroll-btn:hover {
  border-color: #37cf37;
  background: rgba(55, 207, 55, 0.05) !important;
}

.theme--dark .scroll-btn {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

.theme--dark .scroll-btn:hover {
  background: rgba(55, 207, 55, 0.1) !important;
}
</style>
